import type { LiveComment, BannerPos, BannerStyle } from './live-chat';
import type { StoredLayer } from './timeline';
import { announceShort, announceSpent } from './creditEvents';
// Thin API client. The token is the only thing the browser stores; the tenant
// is inside it and is never sent separately, because a client-supplied tenant
// id is exactly what the server refuses to trust.

const TOKEN_KEY = 'rookeryos.token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null): void {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}

export class ApiError extends Error {
  /** 204: a 402 carries what was needed and what is held, so the studio can say the shortfall and offer the packs. */
  constructor(public status: number, message: string, public detail?: Record<string, unknown>) { super(message); }
}

/**
 * Fetch an artifact with the session's bearer token and hand it to the user as
 * a saved file. The object URL is same-origin, carries no credential, and is
 * revoked immediately — so nothing durable is left behind for it.
 */
async function download(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`/v1${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) { setToken(null); throw new ApiError(401, 'session expired'); }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? 'could not fetch that file');
  }
  const url = URL.createObjectURL(await res.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoking on the next tick rather than immediately: Safari has historically
    // cancelled the download if the object URL disappears in the same frame.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * One frame of a card's programme, as an object URL for an <img>.
 *
 * A POST carrying the script the editor currently holds, so the frame follows
 * an unsaved change — changing the shot and seeing nothing until Save is the
 * whole feature missing. `signal` is not optional in practice: this is called
 * as the creator types, and without it a slow early frame can land after a
 * fast later one and show the wrong line.
 *
 * Fetched rather than pointed at, because an <img src> cannot carry a bearer
 * token and this frame is a tenant's unpublished editorial.
 *
 * The caller owns the returned URL and MUST revoke it. An object URL lives
 * until the document does, and this one is replaced on every edit.
 */
export async function fetchFrame(
  cardId: string,
  line: number,
  body: { rundown?: Turn[]; title?: string } = {},
  signal?: AbortSignal,
): Promise<string> {
  const token = getToken();
  const res = await fetch(`/v1/cards/${cardId}/frame`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ line, ...body }),
    signal,
  });
  if (res.status === 401) { setToken(null); throw new ApiError(401, 'session expired'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, err.error ?? 'could not draw that frame');
  }
  return URL.createObjectURL(await res.blob());
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (res.status === 401) { setToken(null); throw new ApiError(401, 'session expired'); }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    // 204: a refusal on credits is a decision, not a failure — the pill and the
    // toast hear it, and the caller gets the shortfall to say.
    if (res.status === 402 && body.reason === 'credits') {
      announceShort({ needed: body.needed ?? null, shortBy: body.shortBy ?? null, balance: body.balance ?? null, message: body.error ?? 'not enough credits' });
    }
    throw new ApiError(res.status, body.error ?? 'request failed', body);
  }
  // Anything that was not a read may have spent credits; the pill re-reads the balance.
  if ((init.method ?? 'GET') !== 'GET') announceSpent();
  // A delete answers 204 with no body; there is nothing to parse.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const SOCIAL_PLATFORMS = [
  { id: 'youtube', label: 'YouTube', glyph: '▶' },
  { id: 'instagram', label: 'Instagram', glyph: '◎' },
  { id: 'facebook', label: 'Facebook', glyph: 'f' },
  { id: 'tiktok', label: 'TikTok', glyph: '♪' },
  { id: 'x', label: 'X', glyph: '𝕏' },
  { id: 'threads', label: 'Threads', glyph: '@' },
  { id: 'website', label: 'Website', glyph: '⌂' },
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]['id'];

/** The social bar's line (F8): "▶ @handle · ◎ @handle", in the platforms' order. */
export function socialLine(handles: Partial<Record<SocialPlatform, string>> | null | undefined): string {
  return SOCIAL_PLATFORMS
    .filter((p) => handles?.[p.id]?.trim())
    .map((p) => `${p.glyph} ${handles![p.id]!.trim()}`)
    .join('  ·  ');
}

/** 190: where the director may draw from — the library, only this episode's collection, or nothing of the show's. */
export type Sourcing = 'library' | 'collection' | 'fresh';
/** 190: how a preset means an episode to be made. */
export type Mode = 'one-shot' | 'directed';

/** 192: one template in the store — free to take, made to be changed. */
export interface Template {
  id: string; source: 'template' | 'look'; kind: 'plate' | 'rig' | 'frame' | 'scene' | 'element' | 'audio' | 'background';
  name: string; blurb: string | null; tags: string[]; licence: 'platform' | 'cc0' | 'creator-shared'; creator: string; updatedAt: string;
  /** The poster's path on the API, or null. */
  poster: string | null;
}

/** 195: what a portrait is doing, answered by the server so a reload cannot lose it. */
export type PortraitState = 'none' | 'drawing' | 'developing' | 'ready' | 'error';

/** 191: talent — a name, what they look like (the sheet), a portrait, a voice, a bio. Workspace-wide. */
export interface TalentRow {
  id: string; name: string; sheet: string | null; portrait_asset_id: string | null;
  voice_id: string | null; bio: string | null; created_at: string; updated_at: string;
  /** 195: none | drawing | developing | ready | error. */
  portrait: PortraitState;
  /** 195: portraits a redraw replaced, newest first. Already paid for. */
  portrait_takes: string[];
}

/** 095: one card preset — a named set of defaults; 190: and where the director draws from, and how. */
export interface CardPreset { id: string; label: string; defaults: CardDefaults; sourcing?: Sourcing; mode?: Mode }

/** 094: one saved element of a show. */
export interface BrandElement { id: string; label: string; props: LayerProps; seconds?: number }

/** 200: one thing the director thinks should happen next on a project. */
/** The story, written before anything else exists (202). */
export interface Storyline {
  logline: string;
  arc: Array<{ part: string; what: string }>;
  beats: Array<{ beat: string; what: string }>;
  characters: Array<{ name: string; role: string; sheet: string }>;
  ending: string;
  idea: string;
  at: string;
}

/** Somebody who could be, or is, in an episode (201). */
export interface CastMember {
  id: string; name: string; hasPortrait: boolean; hasVoice: boolean;
}

export interface CardStep {
  id: string; bench: string; move: string; title: string; why: string;
  weight: number; blocked?: boolean;
}
/** 200: the director's answer — one action, and where it would be done. */
export interface Idea {
  action: string; say: string; args: Record<string, string | number>;
  bench: string | null; move: string | null; inline: boolean;
}
/** 200: one turn on a project's thread. */
export interface IdeaTurn {
  id: number; at: string; role: 'creator' | 'director'; text: string;
  proposal: Idea | null; status: 'open' | 'accepted' | 'dismissed' | 'none';
}

/** 199: one row of the Start here checklist. */
export interface StartStep {
  id: string; done: boolean; why: string; detail: string | null;
  bench: string; move: string | null;
}

export interface Brand {
  id: string; name: string; slug: string;
  /** On the door (119): the public page's slug, words and face. */
  catalogue_slug?: string | null; bio?: string | null; avatar_key?: string | null;
  // The kit. Null means "no kit yet", which the render plane answers with the
  // house palette — deliberately, so a first-day brand looks finished.
  accent_hex: string | null; ground_hex: string | null; wordmark: string | null;
  /** 190: how the show's pictures look; in front of every picture prompt made on it. */
  style?: string | null;
  /** D17: the show's language, BCP-47; null is English. */
  language?: string | null;
  /** Speaker name to voice. Absent speakers keep the positional default. */
  voices: Record<string, VoiceId> | null;
  /** Speaker name to a picture in the library, drawn as that speaker's panel. */
  portraits?: Record<string, string> | null;
  /** Who each speaker is; the script writer is told. */
  bios?: Record<string, string> | null;
  /** Where to follow the show (086): platform → handle, as the platform writes it. */
  handles?: Partial<Record<SocialPlatform, string>> | null;
  /** 094: the show's saved elements — banners, buttons, bugs, lower thirds. */
  elements?: BrandElement[] | null;
  /** 095: named sets of defaults a new card starts from. */
  presets?: CardPreset[] | null;
  /** 097: the show's logos — picture asset ids. */
  logos?: string[] | null;
  /** The transitions this brand keeps to hand; the rest stay in the vault. */
  shelf_transitions?: string[];
  /**
   * What a new card starts with — the recurring theme, the cast, the mix.
   *
   * Copied onto the card at creation, never referenced, so changing this never
   * alters a video somebody already made.
   */
  card_defaults?: CardDefaults;
}

export interface CardDefaults {
  topic?: string;
  painPoint?: string;
  speakers?: string[];
  /** The show's mark: a picture kept in a corner for the whole programme. */
  logo?: { assetId: string; position?: LayerPosition; height?: number };
  /** F10: the wordmark in every lower third's right cell. */
  rig?: { wordmark?: boolean };
  mix?: {
    transition?: { kind: string; seconds?: number };
    /** The closing fade to black, in seconds; absent is the hard stop. */
    fadeOut?: number;
    /** Asset IDS, never storage keys — resolved by the API when applied. */
    music?: { assetId: string; gainDb?: number; duck?: boolean };
    panels?: 'card' | 'box';
    /** F2: the boxes' border — 'light', 'accent' or '#rrggbb' — and width 2–10. */
    box?: { line?: string; width?: number };
    background?: { assetId: string; dim?: number };
  };
}
/** Spyglass (154): a recording — the lab's product from a screen take. */
export interface Recording {
  id: string; brandId: string | null; sourceId: string; sourceName: string; name: string;
  status: 'building' | 'ready' | 'error'; error: string | null;
  videoAssetId: string | null; posterAssetId: string | null;
  transcript: { at: number; text: string }[]; durationMs: number | null; width: number | null; height: number | null;
  shared: boolean; shareUrl: string | null; createdAt: string; updatedAt: string;
  /** 157: what the director read. */
  summary?: string | null; chapters?: { at: number; title: string }[]; readAt?: string | null;
  /** Signed, on the single read only. */
  video?: string | null; poster?: string | null;
}
/** The workspace's defaults (155): what every lab starts from. */
export interface Defaults {
  version: 1;
  bubble?: { corner: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'; size: number; shape: 'circle' | 'rounded'; hidden: boolean };
  plate?: { kind: 'none' | 'still' | 'streaks' | 'worldmap' | 'starburst' | 'bokeh' | 'aurora' | 'grid'; seconds: number };
  lookId?: string | null;
  shareRecordings?: boolean;
  talent?: string | null;
  assetGroup?: string | null;
  /** 204: the model each bench starts from, by catalogue id. */
  models?: Partial<Record<Bench, string>>;
}
/** The director's board (T229): a proposal on a bench. */
export interface Proposal { id: string; bench: string; title: string; why: string; count: number; weight: number; action: string }
/** T237: the director's memo — the board, spoken, once a day. */
export interface Memo { text: string; at: string | null; fresh: boolean }
export interface ScreenTake { id: string; name: string; durationMs: number | null; hasWords: boolean; brandId: string | null; createdAt: string }

/** 199: one shot the director proposed. Not yet bought. */
export interface PlannedScene {
  slug: string; beat: string; action: string; cast: string[];
  speaker: string | null; line: string | null;
}
/** 199: one shot that has been commissioned. */
export interface SceneRow {
  id: string; plan_id: string; ord: number; beat: string; action: string;
  cast_ids: string[]; speaker_id: string | null; line: string | null;
  status: 'building' | 'ready' | 'error'; error: string | null; asset_id: string | null;
}
export interface ScenePlan {
  id: string; brandId: string | null; name: string; premise: string;
  seconds: number; model: string; created_at: string;
  n?: string; ready?: string; failed?: string;
}

/** 197: one beat of an illustrated clip — who says what, in what scene. */
export interface ClipBeat { talentId: string; text: string; read?: string; scene: string; motion?: 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'none' }
export interface ClipRequest {
  name?: string; brandId: string | null; beats: ClipBeat[]; people?: string[]; bedAssetId?: string | null;
  transition?: { kind: string; seconds?: number }; size?: '1K' | '2K';
}
/** 197: a clip the Video Lab built. The row is the build; the product is the video asset. */
export interface Clip {
  id: string; brandId: string | null; name: string; how: 'illustrated' | 'generative'; beats: ClipBeat[];
  status: 'building' | 'ready' | 'error'; error: string | null;
  assetId: string | null; posterAssetId: string | null; pictureAssetIds: string[]; seconds: number | string | null;
  created_at: string; updated_at: string;
}

export interface Asset {
  id: string; brand_id: string | null; kind: 'video' | 'audio' | 'image' | 'document';
  storage_key: string; bytes: number | null; duration_ms: number | null; created_at: string;
  status: 'pending' | 'promoting' | 'ready' | 'error';
  media_key: string | null;
  /** What the picture shows, from the vision pass. */
  tags?: string[];
  description?: string | null;
  described_at?: string | null;
  error: string | null;
  /** 053. What a person calls it, what they wrote about it, and its still. */
  name: string | null;
  notes: string | null;
  poster_key: string | null;
  /** 156: the creator's shelf this sits on, or none. */
  group_name?: string | null;
}

// --- the catalogue and the credits (204) -------------------------------------
export type Bench = 'text' | 'image' | 'edit' | 'music' | 'tts' | 'video' | 'upscale' | 'stt';
export type EditOp = 'prompt' | 'upscale' | 'remove-background' | 'combine';
export type PriceUnit = 'each' | 'second' | 'minute' | 'k_tokens' | 'k_chars';
/** The choices a model offers, each with its credits, from the server's price rows. */
export interface ModelOptions {
  resolutions?: Array<{ id: string; label: string; credits: number; default: boolean }>;
  qualities?: Array<{ id: string; label: string; credits: number; default: boolean }>;
  durations?: Array<{ seconds: number; default: boolean }>;
  audio?: { on: number | null; off: number | null };
  scale?: Array<{ x: 2 | 4; credits: number; default: boolean }>;
  tokens?: { input: number; output: number; cachedInput: number | null };
}
/** One model as the picker shows it: the vendor's name and privacy word reach the client; its cost never does. */
export interface ModelInfo {
  id: string; vendorId: string; vendor: string; family: string; name: string; blurb: string | null;
  /** 205: who wrote the blurb — the vendor, the house's writer, or a curator. */
  blurbBy?: 'vendor' | 'house' | 'curator';
  privacy: 'private' | 'anonymized' | 'unknown'; status: 'active' | 'deprecated';
  isNew: boolean; featured: boolean; houseDefault: boolean; workspaceDefault: boolean;
  /** The headline: the default variant's credits per `unit`. */
  credits: number | null; unit: PriceUnit; defaultVariant: string;
  options: ModelOptions;
  cacheDiscount?: number;
  constraints: { promptChars?: number; aspectRatios?: string[]; resolutions?: string[]; durations?: number[]; modelType?: string; audio?: boolean; audioConfigurable?: boolean; minSeconds?: number; maxSeconds?: number };
  replacement?: { id: string; name: string } | null;
  removesAt?: string | null;
}
/** What the creator chose on a model's options. */
export interface Selection { resolution?: string; quality?: string; seconds?: number; audio?: boolean; scale?: 2 | 4 }
export interface QuoteBalance { plan: number; purchased: number; total: number; ok: boolean; shortBy: number; unmetered: boolean }
export interface Quote {
  model: { id: string; name: string; vendor: string } | null; variant: string | null;
  credits: number; unit: string; units: number;
  balance: QuoteBalance;
  /** The price table is more than a tenth off what the vendor quoted today. The table still wins. */
  vendorDrift?: boolean;
}
export interface CreditBalance {
  balance: { plan: number; purchased: number; total: number };
  expiring: Array<{ credits: number; at: string }>;
  monthSpent: number; spendCeiling: number | null; unmetered: boolean;
  plan: { tier: string; monthlyCredits: number | null; centsPer1000: number | null; rolloverDays: number | null; renewsAt: string };
  packs: Array<{ id: string; name: string; credits: number; amount: number; currency: string; ready: boolean }>;
  billing: boolean;
}
export interface LedgerRow {
  id: number; at: string; kind: 'grant' | 'purchase' | 'debit' | 'expiry' | 'refund' | 'adjust'; credits: number; bucket: 'plan' | 'purchased';
  model: { id: string; name: string | null; vendorId: string | null } | null; bench: string | null; variant: string; units: number | null; ref: string; note: string | null; expiresAt: string | null;
}
export interface CreditSummary {
  days: number;
  byModel: Array<{ model: { id: string; name: string }; bench: string | null; units: number; credits: number }>;
  byBench: Array<{ bench: string | null; credits: number }>;
  byDay: Array<{ day: string; credits: number }>;
}
/**
 * A job on the render plane.
 *
 * This was typed Record<string, unknown>[] and discarded by its only caller, so
 * every field the API computes per request — the queue position, whether a
 * lease has expired under a dead worker, how many pieces of footage a job will
 * cut to — arrived and was thrown away. Typed now because it is about to be
 * shown.
 */
export interface RenderJob {
  id: string;
  brand_id: string | null;
  tier: string;
  status: 'queued' | 'running' | 'complete' | 'failed' | 'cancelled';
  attempts: number;
  error: string | null;
  output_key: string | null;
  lease_holder: string | null;
  lease_until: string | null;
  lease_expired: boolean;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  kind: string | null;
  title: string | null;
  card_id: string | null;
  source_count: number;
  cpu_seconds: string | number | null;
  queue: { position: number; aheadOfYou: number } | null;
}

export interface SignedUrl { url: string; key: string; expiresAt: string }

export interface Moment { at: number; text: string }
/** D8: the director's short — where it starts, how long, and why. */
export interface ShortPick { start: number; duration: number; why: string; score: number }
export interface Chapter { at: number; title: string }
export interface Finding { severity: 'blocker' | 'warning'; says: string; fix?: string; /** D6: where in the cut, in seconds. */ at?: number }
export interface SeoPack {
  seo_title: string | null;
  seo_description: string | null;
  seo_tags: string[] | null;
}

export interface Short {
  id: string;
  status: 'queued' | 'running' | 'complete' | 'failed' | 'cancelled';
  error: string | null;
  created_at: string;
  finished_at: string | null;
  start_seconds: string | number | null;
  duration_seconds: string | number | null;
  /** S9: a short, or the teaser. */
  kind?: 'short' | 'teaser';
}

export interface Account { id: string; email: string; name: string | null }
export interface Workspace { id: string; name: string; slug: string; role: string }
export interface PlanOut {
  id: string; name: string; tier: string; description: string | null; features: unknown;
  /** 204: the month's grant, what a thousand credits cost on this plan, and how long an unspent lot lives. */
  credits: number | null; centsPer1000: number | null; rolloverDays: number;
  prices: { id: string; currency: string; interval: string; amount: number | null }[];
  packs: { id: string; name: string; credits: number; amount: number; currency: string }[];
}
export interface BillingSummary {
  configured: boolean; tier: string;
  entitlement: { tier: string; source: 'default' | 'plan' | 'override'; planId: string | null; override: { tier: string; reason: string | null } | null; appliedAt: string } | null;
  subscription: { id: string; planId: string | null; priceId: string | null; amount: number; currency: string; interval: string; status: string; periodStart: string | null; periodEnd: string | null; cancelAtPeriodEnd: boolean; canceledAt: string | null } | null;
  invoices: { id: string; status: string; amountDue: number; amountPaid: number; currency: string; url: string | null; pdf: string | null; periodStart: string | null; periodEnd: string | null; createdAt: string }[];
  connect: { chargesEnabled: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean } | null;
  portal: boolean;
  usage: Partial<Record<'render_minutes' | 'publishes' | 'storage_bytes' | 'live_minutes' | 'images' | 'tts_chars' | 'stt_seconds' | 'llm_input_tokens' | 'llm_output_tokens', number>>;
  meteredAt: string | null;
  /**
   * What is LEFT, live, rather than what the rollup last said was spent.
   * `limit: null` means unmetered on this tier — draw no bar against it.
   */
  allowances: {
    images: Allowance; voiceChars: Allowance; transcriptionSeconds: Allowance; renderMinutes: Allowance;
    /** 196: seconds of made music. */
    musicSeconds?: Allowance;
    episodes: Allowance;
    platforms: { limit: number | null };
    /** Minutes our own crashes cost, recorded and never charged (163). */
    wastedMinutes: number;
  };
  trial: TrialState;
  archive: ArchiveState | null;
  /** 204: the two buckets. */
  credits?: { plan: number; purchased: number; total: number; unmetered: boolean };
}
export interface Allowance { used: number; limit: number | null; soft: boolean }
/** The sixty days (179). `onTrial` false once somebody is paying. */
export interface TrialState { endsAt: string | null; onTrial: boolean; lapsed: boolean; daysLeft: number }
/** Where finished work lives (180). null means no archive has ever been connected. */
export interface ArchiveState {
  provider: string; accountEmail: string | null; folderUrl: string | null;
  status: 'connected' | 'expired' | 'revoked' | 'error';
  keepOurCopy: boolean; objectsArchived: number; bytesArchived: number; objectsWaiting: number;
}

export type ConceptKind = 'person' | 'place' | 'organisation' | 'product' | 'topic' | 'claim' | 'question' | 'moment';
export interface CardIndex {
  takeaways: string[];
  playsByDay: { day: string; plays: number }[];
  document: { status: string; reason: string | null; indexedAt: string | null; usedModel: boolean; error: string | null; transcriptChars: number; chapters: number; rebuildsLeft: number } | null;
  concepts: { id: string; kind: ConceptKind; term: string; at: number | null; evidence: string | null; source: 'ai' | 'creator'; status: 'kept' | 'removed' }[];
  door: { plays: number; likes: number; comments: number; followers: number };
  manifest: { issuedAt: string; signed: boolean } | null;
}

export interface WeekPlan {
  episodes: { title: string; angle: string; why: string; signal: 'question' | 'plays' | 'comments' | 'gap'; question: string | null }[];
  signals: { questions: number; topPlayed: number; comments: number; recent: number } | null;
  plannedAt: string | null;
}

export interface DoorEpisode {
  id: string; title: string; slug: string | null; visibility: 'public' | 'members' | 'unlisted'; publishedAt: string | null;
  series: { name: string; part: number | null } | null;
  plays: number; playsWeek: number; likes: number; comments: number; unanswered: number; saved: number; indexed: boolean; takeaways: number;
}

export interface DoorStatus {
  billing: boolean;
  shows: { id: string; name: string; slug: string | null; hasBio: boolean; hasAvatar: boolean; url: string | null }[];
  episodes: { published: number; public: number; members: number; renderedUnpublished: number };
  tiers: { active: number; ready: number };
  payouts: { chargesEnabled: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean } | null;
  connectors: number; followers: number; plays: number; playsWeek: number; playsPrevWeek: number; reminders?: number;
  flags: { review: number; hidden: number };
  unanswered: { cardId: string; title: string; count: number }[];
  topByPlays: { cardId: string; title: string; topic: string | null; painPoint: string | null; plays: number }[];
}

export interface MembersSummary {
  configured: boolean;
  tiers: { id: string; brandId: string; name: string; description: string | null; amount: number; currency: string; interval: string; active: boolean; ready: boolean; createdAt: string }[];
  shows: { brandId: string; members: number; monthly: number; earned: number; fees: number; refunded: number }[];
  members: { id: string; brandId: string; tierId: string | null; status: string; amount: number; currency: string; interval: string; periodEnd: string | null; cancelAtPeriodEnd: boolean; since: string }[];
}

export interface CardComment { id: string; parentId: string | null; body: string; status: 'pending' | 'shown' | 'hidden'; createdAt: string; authorId: string; fromShow?: boolean; waiting?: boolean }

export interface LoginResult {
  token: string;
  user: Account;
  tenant: Workspace;
  workspaces: Workspace[];
}

export interface HookVariant {
  line: string; angle: string; hold: number;
  verdict: 'strong' | 'testing' | 'weak'; why: string;
}

/**
 * A proposal from the assistant director, and the decision attached to it.
 *
 * `payload` is the thing itself in the shape the layer table takes; `rationale`
 * is why, in a sentence, which is the part a creator actually decides from.
 */
export interface CardVersion {
  id: string;
  /** Monotonic per card, so a creator can say "take me back to 4". */
  seq: number;
  label: string;
  fingerprint: string | null;
  /** True when this exact configuration already has a finished render. */
  rendered: boolean;
  created_at: string;
}

export interface Suggestion {
  id: string;
  card_id: string;
  /**
   * 'layer' becomes a row beside the script; 'source' is an EDIT to the script
   * itself. The studio has to tell them apart, because the second one changes
   * something the creator considers theirs and should say so before they
   * approve it.
   */
  kind: 'layer' | 'source' | 'music';
  rationale: string;
  payload: {
    /** Layer and source. A bed is not anchored: it runs under everything. */
    anchorId?: string;
    /** Layer only. */
    offsetSeconds?: number;
    seconds?: number;
    props?: { text: string; position?: LayerPosition; style?: LayerStyle; sub?: string; url?: string };
    /** Source only: the clip to cut this scene to. */
    sourceId?: string;
    /** Music only: the audio asset that becomes the bed. */
    assetId?: string;
    gainDb?: number;
    duck?: boolean;
  };
  drawn_from: 'shelf' | 'vault';
  status: 'pending' | 'approved' | 'denied';
  decided_at: string | null;
  applied_layer_id: string | null;
  created_at: string;
}

export interface Turn {
  /**
   * The scene's identity, minted by the API on save.
   *
   * Carry it through every edit. The studio does this naturally because it
   * spreads the turn object rather than rebuilding it — but anything that
   * constructs a fresh turn from parts would drop the id, and the API would
   * mint a new one, which strands whatever was anchored to that scene.
   */
  id?: string;
  speaker: string; text: string;
  emotion: 'neutral'|'warm'|'urgent'|'amused'|'serious'|'curious';
  gesture: 'none'|'nod'|'lean-in'|'point'|'open-hands'|'count';
  shot: 'single'|'two-shot'|'wide'|'screen-share'|'insert'|'boxed'|'graphic';
  beat: number;
  /**
   * Hold this scene for exactly this long, instead of for as long as its
   * narration runs. A scene with a hold and no text is an INSERTED scene — a
   * sponsor, a product shot, a title card.
   *
   * It ripples: a scene is a member of the spine, so inserting one moves
   * everything after it, and every layer with it, because layers anchor to a
   * scene id rather than to a clock.
   */
  holdSeconds?: number;
  /** Cut to real footage for this line instead of drawing a panel. */
  sourceId?: string | null;
  sourceIn?: number;
  sourceOut?: number;
  /**
   * The rectangle of the source to show, in FRACTIONS of its width and height.
   * Not pixels: a rectangle that frames a face in a 4K clip is somewhere in the
   * corner of a 720p one, and a source can be replaced.
   */
  crop?: { x: number; y: number; w: number; h: number } | null;
  /** How fast to play the shot. The picture only — the line is narrated over it. */
  speed?: number | null;
  audio?: 'over'|'sync'|'mixed';
  /** F7: a `title` scene is the show open — `text` set large, `sub` under it, held. */
  kind?: 'line'|'title';
  sub?: string;
  /**
   * Movement on a still cut into this line — a slow push or drift.
   *
   * Only meaningful when the footage IS a still: a clip already moves, and the
   * render plane drops this rather than fighting the picture.
   */
  motion?: { kind: MotionKind; amount?: number } | null;
  /** The insert framing's corner for the speaker's face. */
  inset?: { position: InsetPosition } | null;
  /**
   * A second camera on a footage scene: another source's picture in the
   * corner (`pip`) or beside the first (`split`). The first source keeps the
   * sound and the length; this one is picture only, from `sourceIn`.
   */
  second?: { sourceId: string; layout: 'pip' | 'split'; position?: InsetPosition; sourceIn?: number } | null;
  /**
   * The graphic shot's panel (F3): a stacked headline and up to three
   * pictures from this episode's collection, by asset id; `side` is where
   * the HOST sits.
   */
  graphic?: { headline?: string; pictures: string[]; side: 'left' | 'right' } | null;
  /** How to read the line, in words — honoured by a directed voice. */
  read?: string;
  /**
   * How this line joins the one BEFORE it. Absent means "whatever the card
   * says" — the card-wide mix.transition, which is all a card could express
   * before joins became individual.
   */
  transition?: { kind: TransitionKind; seconds?: number } | null;
}

/**
 * How a line is framed — who is in the picture.
 *
 * The renderer honours these (programme.panels_for); `screen-share` and
 * `insert` mean "the picture is not the panels", which only happens on a line
 * that cuts to footage, so they render as `wide` on a drawn panel.
 */
export const SHOTS = [
  { id: 'wide',         label: 'wide',        hint: 'everyone in the cast' },
  { id: 'single',       label: 'single',      hint: 'just the speaker, close' },
  { id: 'two-shot',     label: 'two-shot',    hint: 'the speaker and one other' },
  { id: 'screen-share', label: 'screen share', hint: 'for a line cutting to footage' },
  { id: 'insert',       label: 'footage + face', hint: 'the speaker over their footage, in the corner' },
  { id: 'boxed',        label: 'footage in a box', hint: 'the clip in a bordered box on the set, the line beneath it' },
  { id: 'graphic',      label: 'host + graphic', hint: 'the speaker in a third of the frame, a headline and pictures beside them' },
] as const;

export type ShotKind = (typeof SHOTS)[number]['id'];

/**
 * Where a layer sits in the frame.
 *
 * Pinned here, in the API's zod enum and in 066's CHECK; the render plane's
 * layers.POSITIONS is the authority, because it is the thing that has to place
 * them.
 */
export const LAYER_POSITIONS = [
  { id: 'bottom',       label: 'bottom' },
  { id: 'top',          label: 'top' },
  { id: 'top-left',     label: 'top left' },
  { id: 'top-right',    label: 'top right' },
  { id: 'bottom-left',  label: 'bottom left' },
  { id: 'bottom-right', label: 'bottom right' },
] as const;

export type LayerPosition = (typeof LAYER_POSITIONS)[number]['id'];

export const INSET_POSITIONS = [
  { id: 'bottom-right', label: 'bottom right' },
  { id: 'bottom-left',  label: 'bottom left' },
  { id: 'top-right',    label: 'top right' },
  { id: 'top-left',     label: 'top left' },
] as const;
export type InsetPosition = (typeof INSET_POSITIONS)[number]['id'];

/** What a piece of anchored text is: a banner, a button, a bug. */
export const LAYER_STYLES = [
  { id: 'banner',      label: 'banner',      hint: 'a plate with the accent rail' },
  { id: 'lower-third', label: 'lower third', hint: 'the full-width band: a headline and a line beneath' },
  { id: 'button',      label: 'button',      hint: 'a pill in the accent colour — a call to action' },
  { id: 'bug',         label: 'bug',         hint: 'small and quiet, the mark in a corner' },
  { id: 'ticker',      label: 'ticker',      hint: 'a crawl along the foot — the words scroll for the window' },
] as const;
export type LayerStyle = (typeof LAYER_STYLES)[number]['id'];
export const LAYER_FONTS = [
  { id: 'sans',  label: 'sans' },
  { id: 'serif', label: 'serif' },
  { id: 'mono',  label: 'mono' },
] as const;
export type LayerFont = (typeof LAYER_FONTS)[number]['id'];

/** The tags every show wears, one click each (F6). Props for a text layer. */
export const TAG_PRESETS: Array<{ id: string; label: string; props: LayerProps; seconds: number }> = [
  { id: 'live',       label: 'LIVE',       seconds: 3600, props: { text: 'LIVE', style: 'button', position: 'top-left', size: 30 } },
  { id: 'coming-up',  label: 'Coming up',  seconds: 6,    props: { text: 'Coming up', style: 'banner', position: 'bottom-left', size: 34 } },
  { id: 'debate',     label: 'DEBATE',     seconds: 3600, props: { text: 'DEBATE', style: 'button', position: 'top-left', size: 30 } },
  { id: 'highlights', label: 'HIGHLIGHTS', seconds: 3600, props: { text: 'HIGHLIGHTS', style: 'bug', position: 'top-right', size: 24 } },
  { id: 'vs',         label: 'VS',         seconds: 6,    props: { text: 'VS', style: 'button', position: 'bottom-left', size: 40 } },
  // F17: the quiet ask every channel makes, bottom right, for the whole programme.
  { id: 'subscribe',  label: 'SUBSCRIBE',  seconds: 3600, props: { text: 'SUBSCRIBE', style: 'button', position: 'bottom-right', size: 26 } },
  { id: 'new',        label: 'NEW',        seconds: 3600, props: { text: 'NEW', style: 'button', position: 'top-right', size: 26 } },
  // F11: the archive date, small, top left, for the first seconds of a scene
  // that cuts to filed footage. The text is set when it is placed: the
  // scene's source's own date, else today's.
  { id: 'date',       label: 'Date (archive)', seconds: 6, props: { text: '00.00.00', style: 'bug', position: 'top-left', size: 22 } },
  // F15: the app / SMS band under the picture for the whole stream — the
  // instruction in caps, a picture cell on the left once one is chosen.
  { id: 'cta',        label: 'CTA band',   seconds: 3600, props: { text: "Text 'APP' to 45470", style: 'lower-third', position: 'bottom', size: 34, sub: 'to download the app' } },
];

/** MM.DD.YY — the stamp the thumbnail and the archive tag share. */
export function dateStamp(d: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getMonth() + 1)}.${two(d.getDate())}.${String(d.getFullYear()).slice(-2)}`;
}

/** How a layer's text is set. Everything past `text` is the creator's to change. */
export interface LayerProps {
  text?: string; position?: LayerPosition; style?: LayerStyle; size?: number; font?: LayerFont;
  bold?: boolean; italic?: boolean; underline?: boolean; color?: string;
  /** The second line of a lower third. */
  sub?: string;
  /** F12: a lower third with a URL draws it as a QR code in its right cell. */
  url?: string;
  /** A picture layer: which picture, drawn how tall. A sound cue: which audio. */
  assetId?: string; height?: number;
  /** A sound cue: its level under the speech, and its fades. */
  gainDb?: number; fadeIn?: number; fadeOut?: number;
}

export const MOTIONS = [
  { id: 'none',       label: 'hold' },
  { id: 'zoom-in',    label: 'push in' },
  { id: 'zoom-out',   label: 'pull out' },
  { id: 'pan-left',   label: 'drift left' },
  { id: 'pan-right',  label: 'drift right' },
] as const;

export type MotionKind = (typeof MOTIONS)[number]['id'];

export interface RenderJob {
  id: string; status: 'queued'|'running'|'complete'|'failed'|'cancelled';
  output_key: string | null; thumbnail_key: string | null; caption_key: string | null;
  /** The finished cut's sound alone, as an MP3 (086), or null. */
  audio_key?: string | null;
  error: string | null; attempts: number;
  created_at: string; finished_at: string | null;
}

export interface Publication {
  id: string; status: 'queued'|'uploading'|'published'|'failed';
  scheduled_for: string | null; remote_url: string | null; error: string | null;
  platform: string; account_handle: string | null;
  /** S9: the episode, a teaser, or a short. */
  kind?: 'episode' | 'teaser' | 'short';
  /** D11: what the audience did, as the platform last reported it. */
  stats?: { views?: number; likes?: number; comments?: number; error?: string } | null;
  stats_at?: string | null;
}

export type SourceKind = 'camera'|'microphone'|'screen'|'tape'|'file'|'stream';
/** What the speech-to-text service heard: segments with times, on the source's clock. */
export interface Transcript {
  model?: string;
  duration?: number | null;
  segments: Array<{ start: number; end: number; text: string }>;
}

export interface Source {
  id: string; brand_id: string | null; name: string; kind: SourceKind;
  mode: 'stored'|'live';
  /** The asset a stored source was made from, so a drop can reuse it rather than mint a second. */
  asset_id?: string | null;
  source_key: string | null; normalized_key: string | null;
  ingest_protocol: string | null; ingest_url: string | null;
  status: 'pending'|'ingesting'|'ready'|'error'|'live'|'offline';
  duration_ms: number | null; width: number | null; height: number | null;
  /** SN3: chapters the director marked on a take, and whether its words (with times) are in. */
  chapters?: DemoChapter[] | null; has_words?: boolean;
  fps: string | number | null; has_audio: boolean | null; interlaced: boolean | null;
  error: string | null;
  /** When it was filed — the archive date tag (F11) reads it. */
  created_at?: string;
  /** What the speech-to-text service heard, if anyone asked (085). */
  transcript_status?: 'none' | 'transcribing' | 'ready' | 'error';
  transcript_error?: string | null;
  /**
   * On the creator's shelf — the working set the assistant director reaches for
   * by default. Everything else is the vault: still here, still usable by hand,
   * offered to the director only when asked to range wider.
   */
  on_shelf?: boolean;
  /** 090/091: what was said on a camera take — typed, or transcribed at ingest. */
  said?: string | null;
  /** T238: what the director read on it. */
  summary?: string | null; read_at?: string | null;
}

/**
 * What the API says a platform can do, and whether this deployment can do it.
 *
 * The studio used to keep its own list of six platform names and its own set of
 * one connectable name, and the API kept a third — so the screen offered
 * destinations the publisher would refuse. `ready` is the only flag a button
 * should consult; `configured` and `implemented` are for saying WHY not.
 */
export interface PlatformReport {
  platform: string;
  label: string;
  capabilities: string[];
  configured: boolean;
  implemented: boolean;
  ready: boolean;
  missing: string[];
}

export interface Connector {
  id: string; brand_id: string; brand_name: string; platform: string;
  account_handle: string | null; status: 'connected'|'expired'|'revoked'|'error';
  has_token: boolean; last_error: string | null;
}

export interface ScheduledPublication {
  id: string;
  status: 'queued' | 'uploading' | 'published' | 'failed';
  scheduled_for: string | null;
  published_at: string | null;
  remote_url: string | null;
  error: string | null;
  /** D11: what the audience did, as the platform last reported it. */
  stats?: { views?: number; likes?: number; comments?: number } | null;
  attempts: number;
  platform: string;
  account_handle: string | null;
  connector_status: string;
  connector_ready: boolean;
  card_id: string;
  card_title: string;
  card_stage: Stage;
  brand_name: string | null;
  render_status: string | null;
  output_key: string | null;
}

export interface CardDetail {
  /**
   * The transitions this card's brand keeps to hand.
   *
   * Sent with the card so the editor has something to offer without a second
   * round trip and without a list of its own. An unbranded card gets the four
   * that shipped first.
   */
  shelfTransitions?: string[];
  card: Card; rundown: Turn[]; estimateSeconds: number;
  /** Per line: measured by the last render when it matches this script, estimated otherwise. */
  lineSeconds?: number[];
  /** Whether lineSeconds came from a render or from the 150-words-a-minute rule. */
  lineSecondsAreMeasured?: boolean;
  speakers: string[];
  job: RenderJob | null; publications: Publication[]; shorts: Short[];
}

export type Stage = 'idea'|'scripted'|'filming'|'editing'|'review'|'scheduled'|'published';
import type { Look, Recipe } from './lab/recipe';
export type { Look, Recipe };

export interface Series { id: string; brandId: string; name: string; slug: string; description: string | null; episodes: number; createdAt: string }

export interface Card {
  id: string; title: string; stage: Stage; brand_id: string | null;
  /** On Rookery (119): where the local copy shows at rookeryos.com, and its public slug once published. */
  visibility?: 'public' | 'members' | 'unlisted'; slug?: string | null; published_at?: string | null;
  /** Series (142): the run this episode belongs to, and its part. */
  series_id?: string | null; series_order?: number | null;
  master_job_id?: string | null;
  /** The card's own thumbnail (082), or null for the render's. */
  thumbnail_key?: string | null;
  brand_name: string | null; topic: string | null; pain_point: string | null;
  /** D14: the producer's brief — the angle and the points the piece must land. */
  brief?: string | null;
  /** 190: where the director may draw from. */
  sourcing?: Sourcing;
  created_at: string;
  /** When anything on the card last changed — the deck's brief reads it. */
  updated_at?: string;
  seo_title: string | null; seo_description: string | null; seo_tags: string[] | null;
  /** 050. What the card should sound like. Null until somebody sets a bed. */
  mix: CardMix | null;
}

export interface CardMix {
  music?: {
    /** The resolved storage key. The studio sends an assetId and reads this. */
    key: string;
    gainDb?: number;
    duck?: boolean;
    fadeIn?: number;
    fadeOut?: number;
  };
  background?: { key: string; dim?: number };
  transition?: { kind: TransitionKind; seconds?: number };
  /** The closing fade to black, in seconds; absent is the hard stop. */
  fadeOut?: number;
  /** false keeps each spoken line off the screen; absent is shown. */
  captions?: boolean;
  /** F2: cards (default) or broadcast boxes. */
  panels?: 'card' | 'box';
  /** F2: the boxes' border and width; absent is the light default. */
  box?: { line?: string; width?: number };
  /** The Lab (153): the look's rendered overlay, looped over the finished cut. */
  look?: { id: string; overlayKey: string };
  /** The finished cut's loudness in LUFS; absent is −16, the podcast norm. */
  loudness?: number;
  /** How hard the encoder works; absent is standard. */
  quality?: QualityName;
  /** How the burned-in line looks; absent is the house caption. */
  captionStyle?: CaptionStyle;
}

export type QualityName = 'draft' | 'standard' | 'high';

/** How the burned-in line looks: where, how big, on a plate or outlined, in which face. */
export interface CaptionStyle {
  position?: 'bottom' | 'top';
  size?: 'small' | 'medium' | 'large';
  plate?: boolean;
  font?: LayerFont;
}

/** What a card may ask of the mix pass, and what each choice means on screen. */
export const LOUDNESS_TARGETS = [
  { value: -14, label: '−14 LUFS · video platforms' },
  { value: -16, label: '−16 LUFS · podcast (default)' },
  { value: -18, label: '−18 LUFS · quiet' },
] as const;

export const QUALITIES: Array<{ id: QualityName; label: string; hint: string }> = [
  { id: 'draft',    label: 'draft',    hint: 'quick to render; fine for a check, not for publishing' },
  { id: 'standard', label: 'standard', hint: 'the house setting' },
  { id: 'high',     label: 'high',     hint: 'about twice as long to render; for a master worth keeping' },
];

export interface MixInput {
  assetId: string;
  gainDb?: number;
  duck?: boolean;
  fadeIn?: number;
  fadeOut?: number;
}

/**
 * A transition's name, as the render plane spells it.
 *
 * Deliberately `string` and not a union. There are fifty-nine of them, the
 * render image is the authority on which, and the studio used to hold TWO
 * copies of the list — this type and the per-join control's options array —
 * which were two of five hand-maintained statements of one fact. At four values
 * that was survivable; at fifty-nine it is not. The editor is given its brand's
 * shelf with the card and asks /v1/catalog/transitions for the vault.
 *
 * The check that a kind is real happens where it can be real: the zod enum in
 * the API, the CHECK on cards.mix, and the renderer that has to honour it.
 * A union here would be a fourth copy pretending to be a safety net.
 */
export type TransitionKind = string;

/** The four the product shipped with, and what an unbranded card falls back to. */
export const LEGACY_TRANSITIONS = ['cut', 'dissolve', 'fade', 'fadewhite'] as const;

/** Names a creator reads. Anything not here shows as ffmpeg spells it. */
export const TRANSITION_LABELS: Record<string, string> = {
  cut: 'Cut',
  dissolve: 'Dissolve',
  fade: 'Fade through black',
  fadewhite: 'Fade through white',
  'grain-dissolve': 'Grain dissolve',
};

export function transitionLabel(kind: string): string {
  return TRANSITION_LABELS[kind] ?? kind;
}

/** Every transition the render plane can do, and the four a card can fall back to. */
export const catalogTransitions = () =>
  request<{ transitions: string[]; shipped: string[]; degraded?: boolean }>('/catalog/transitions');

/**
 * The voices the render image ships, and what to call them on screen.
 *
 * The ids are pinned in three places — here, migration 062's CHECK, and the
 * render image's VOICE_ORDER. The database is the one that refuses; this list
 * exists so the studio can offer a name a person recognises instead of a model
 * filename. A render-plane test compares 062's list against VOICE_ORDER.
 */
export const VOICES = [
  { id: 'en_US-lessac-medium', label: 'Lessac',  hint: 'US · warm, even' },
  { id: 'en_US-amy-medium',    label: 'Amy',     hint: 'US · bright' },
  { id: 'en_US-ryan-high',     label: 'Ryan',    hint: 'US · low, unhurried' },
  { id: 'en_GB-alba-medium',   label: 'Alba',    hint: 'UK · Scottish' },
] as const;

/** Any voice the catalogue knows; the four above are the local ones. */
export type VoiceId = string;

/** One voice in the catalogue: local (in the image), venice (Venice's models), cloud (the accents), directed (takes direction). */
export interface Voice {
  id: string; label: string; tier: 'local' | 'cloud' | 'directed' | 'venice';
  language: string; accent: string; gender?: 'female' | 'male'; hint?: string;
}

/** A voice that costs a call to hear: anything not in the render image. */
export function isCloudVoice(id: string | undefined | null): boolean {
  return Boolean(id && /^(gcp|gemini|venice):/.test(id));
}

/** What to call a voice id when the catalogue has not loaded yet. */
export function voiceLabel(id: string, list?: Voice[]): string {
  const v = list?.find((x) => x.id === id) ?? VOICES.find((x) => x.id === id);
  if (!v) return id.replace(/^(gcp|gemini):/, '').replace(/^venice:[^/]+[/]/, '');
  return 'accent' in v ? `${v.label} — ${v.accent}` : `${v.label} — ${v.hint}`;
}

export interface BackdropInput {
  assetId: string;
  /** Optional. The render MEASURES what the image needs; this can only darken. */
  dim?: number;
}

/** 084. A set of finished cuts pushed to a social ingest as if live. */
export interface Channel {
  id: string; brand_id: string | null; name: string; card_ids: string[];
  mode: 'loop' | 'once'; platform: 'youtube' | 'facebook' | 'rtmp';
  ingest_url: string; rig: ChannelRig | null;
  /** P7: the other places the same push goes. */
  extra: Array<{ platform: 'youtube' | 'facebook' | 'rtmp'; ingest_url: string; ingest_secret: string }> | null;
  status: 'off' | 'starting' | 'live' | 'stopping' | 'failed';
  job_id: string | null; started_at: string | null; stopped_at: string | null;
  /** 088: when it starts and stops on its own. */
  start_at: string | null; stop_at: string | null;
  /** 089: how far the push is, and when it last said so. */
  pushed_seconds: number | string; last_pushed_at: string | null;
  error: string | null; created_at: string;
}
/** The live rig, burned onto the cuts once when the channel starts. */
export interface ChannelRig { badge?: string | null; ticker?: string | null; mark?: 'brand' | 'none'; accent?: string | null; /** The Lab (153): a made look baked over every cut. */ look?: string | null }
export interface ChannelInput {
  name: string; brandId: string | null; cardIds: string[];
  mode: 'loop' | 'once'; platform: 'youtube' | 'facebook' | 'rtmp'; ingestUrl?: string;
  rig?: ChannelRig;
}

/** 087: a show's door, and who is at it. */
export interface Room {
  id: string; brand_id: string | null; channel_id: string | null; name: string; slug: string;
  status: 'open' | 'live' | 'closed'; created_at: string; updated_at: string;
  /** G2: set while the room is going out through the media server's egress. */
  egress_id?: string | null; on_air_at?: string | null;
  /** The Lab (153): the look the stage wears. */
  look_id?: string | null;
  /** 143: viewers who asked to hear when this room goes live, not yet told. */
  reminders?: number;
  /** G8/G11: the door's front page and the control room. */
  description?: string | null; rules?: string | null; picture_id?: string | null;
  donate_url?: string | null; donate_label?: string | null; superchat_min?: number; banner_pos?: BannerPos; accent?: string | null;
  banner_x?: string | number | null; banner_y?: string | number | null;
  welcome?: string | null; next_at?: string | null; theme?: 'dark' | 'light'; links?: DoorLink[] | null;
  /** D19: the director's debrief after the live. */
  debrief?: Debrief | null; debriefed_at?: string | null;
  /** D20: the run of show and its clock. */
  plan?: PlanSegment[] | null; plan_started_at?: string | null;
  banner_style?: BannerStyle;
}
export interface PlanSegment { title: string; minutes: number; note?: string }
export interface Debrief { summary: string; asked: string[]; followUps: { title: string; why: string }[]; thank: string[]; nextEpisode: { title: string; angle: string } }
export interface DoorLink { label: string; url: string }
export interface DemoChapter { at: number; title: string }
/** What a visitor sees on the door before they knock. */
export interface DoorInfo {
  name: string; status: string; description?: string | null; rules?: string | null; picture?: string | null;
  donateUrl?: string | null; donateLabel?: string | null; superchatMin?: number; accent?: string | null; bannerPos?: BannerPos; bannerXY?: [number, number] | null;
  welcome?: string | null; nextAt?: string | null; theme?: 'dark' | 'light'; links?: DoorLink[]; logo?: string | null; brandName?: string | null; brandAccent?: string | null;
}
export type RoomComment = LiveComment;
export interface ChatRead { picks: { id: string; why: string }[]; themes: string[]; line: string; watch: { id: string; why: string }[]; poll?: { question: string; options: string[] } | null }
/** G16: a poll on a live, with its tallies (never who). */
export interface RoomPoll { id: string; question: string; options: string[]; status: 'open' | 'closed'; tallies: number[]; created_at: string; closed_at: string | null; mine?: number | null }
export type { BannerPos, BannerStyle };
export type GuestMove = 'admit' | 'hold' | 'on-air' | 'remove' | 'moderator' | 'demote';
export interface GuestProfile {
  role?: string | null; location?: string | null; handle?: string | null; pronouns?: string | null;
  hue?: number | null; avatar?: string | null; question?: string | null; camera_off?: boolean | null;
}
export interface RoomGuest extends GuestProfile {
  id: string; room_id: string; name: string;
  status: 'waiting' | 'admitted' | 'on_air' | 'held' | 'removed' | 'left' | 'moderator';
  joined_at: string; last_seen: string;
}
export interface MediaKey { url: string; token: string; /** G13: a watch-only key — see the room, be seen by nobody. */ watch?: boolean }

/** Thumbs already fetched this session, by path. */
const thumbMemo = new Map<string, Promise<string | null>>();

/**
 * A small picture from the API, kept in the browser's Cache Storage and in
 * memory, so a grid or a table costs nothing the second time. Resolves to
 * an object URL, or null when there is none (404).
 */
async function cachedPicture(path: string): Promise<string | null> {
  const memo = thumbMemo.get(path);
  if (memo) return memo;
  const p = (async () => {
    let res: Response | undefined;
    try { const c = await caches.open('rookery-thumbs'); res = await c.match(path); } catch { /* no cache here */ }
    if (!res) {
      const token = getToken();
      const live = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!live.ok) return null;
      res = live;
      try { const c = await caches.open('rookery-thumbs'); await c.put(path, live.clone()); } catch { /* per-viewer convenience */ }
    }
    return URL.createObjectURL(await res.blob());
  })();
  thumbMemo.set(path, p);
  p.then((u) => { if (!u) thumbMemo.delete(path); }).catch(() => thumbMemo.delete(path));
  return p;
}

/** P3: what the Record screen is filming for — a scene of a card. */
export interface FilmRequest { cardId: string; sceneIndex: number; line: string; title: string }

/** 093: one gathered thing — an asset or a source — on a card. */
export interface CollectionItem {
  item_id: string; added_by: 'creator' | 'director'; note: string | null; added_at: string;
  asset_id: string | null; asset_kind: Asset['kind'] | null; asset_name: string | null; storage_key: string | null;
  asset_status: Asset['status'] | null; asset_duration_ms: number | null;
  source_id: string | null; source_kind: SourceKind | null; source_name: string | null; source_status: Source['status'] | null;
  source_duration_ms: number | null; has_audio: boolean | null; said: string | null;
}

export const api = {
  // --- the Idea Lab (200) -----------------------------------------------
  /** What the director thinks should happen next on ONE project. No model asked. */
  cardNext: (id: string) => request<{ stage: string; scenes: number; cast: CastMember[]; roster: CastMember[]; storyline: Storyline | null; uncast: string[]; steps: CardStep[]; nothing: string | null }>(`/cards/${id}/next`),

  /** Write the story from the idea alone. Nothing else is sent, deliberately. */
  writeStoryline: (id: string, idea: string, minutes?: number) =>
    request<{ storyline: Storyline }>(`/cards/${id}/storyline`, {
      method: 'POST', body: JSON.stringify({ idea, ...(minutes ? { minutes } : {}) }),
    }).then((r) => r.storyline),

  /** Make the story's characters real, and put them in this episode. */
  castFromStory: (id: string, characters: Array<{ name: string; sheet: string; role?: string }>) =>
    request<{ cast: Array<{ id: string; name: string; created: boolean; portrait: 'kept' | 'library' | 'none' }>; talentIds: string[] }>(
      `/cards/${id}/storyline/cast`, { method: 'POST', body: JSON.stringify({ characters }) }),

  /** Say who is in this episode. The whole list, every time. */
  setCardCast: (id: string, talentIds: string[]) =>
    request<{ talentIds: string[] }>(`/cards/${id}/cast`, { method: 'PUT', body: JSON.stringify({ talentIds }) }),
  /** The thread for a project, oldest first. */
  ideaTurns: (id: string) => request<{ turns: IdeaTurn[] }>(`/cards/${id}/idea`).then((r) => r.turns),
  /** One sentence becomes one proposal. Writes two turns and nothing else. */
  askIdea: (id: string, ask: string) =>
    request<{ idea: Idea }>(`/cards/${id}/idea`, { method: 'POST', body: JSON.stringify({ ask }) }),
  decideIdea: (id: string, turn: number, decision: 'accept' | 'dismiss') =>
    request<void>(`/cards/${id}/idea/${turn}/${decision}`, { method: 'POST' }),

  /** 199: where the creator actually is — six steps from empty workspace to published. */
  startHere: () => request<{ steps: StartStep[]; done: number; total: number; next: string | null }>('/start'),

  brands: () => request<{ brands: Brand[] }>('/brands').then((r) => r.brands),
  /**
   * A second brand. POST /v1/brands has existed since the table did and had no
   * caller — a workspace got exactly one brand at signup and could never make
   * another, which for a company running five channels is a hard ceiling.
   */
  createBrand: (name: string, slug: string) =>
    request<{ brand: Brand }>('/brands', {
      method: 'POST', body: JSON.stringify({ name, slug }),
    }).then((r) => r.brand),

  /** A card's layers — the things that ride above the spine. */
  layers: (cardId: string) =>
    request<{ layers: StoredLayer[] }>(`/cards/${cardId}/layers`).then((r) => r.layers),

  addLayer: (cardId: string, body: {
    track: 'text' | 'image' | 'sound'; anchorId: string; offsetSeconds?: number; seconds: number;
    props: LayerProps;
  }) =>
    request<{ layer: StoredLayer }>(`/cards/${cardId}/layers`, {
      method: 'POST', body: JSON.stringify(body),
    }).then((r) => r.layer),

  removeLayer: (cardId: string, layerId: string) =>
    request<void>(`/cards/${cardId}/layers/${layerId}`, { method: 'DELETE' }),

  /** Edit a banner where it is: its text, corner, start and length. */
  updateLayer: (cardId: string, layerId: string, body: {
    offsetSeconds?: number; seconds?: number;
    props?: LayerProps;
  }) =>
    request<{ layer: StoredLayer }>(`/cards/${cardId}/layers/${layerId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }).then((r) => r.layer),

  /**
   * The assistant director's proposals, and what was decided about each.
   *
   * Decided ones are returned too, on purpose. They are the record of what the
   * creator chose — the thing a rollback reads — and a denied suggestion that
   * simply vanishes takes with it the only evidence that the assistant
   * suggested it at all.
   */
  suggestions: (cardId: string) =>
    request<{ suggestions: Suggestion[] }>(`/cards/${cardId}/suggestions`)
      .then((r) => r.suggestions),

  /**
   * Ask the director for proposals. Nothing it returns has touched the card.
   *
   * `scope` decides where it may reach for footage: the creator's shelf by
   * default, the whole vault when they ask it to range wider.
   */
  directCard: (cardId: string, brief?: string, scope: 'shelf' | 'vault' | 'episode' = 'shelf') =>
    request<{ suggestions: Suggestion[]; detail?: string }>(`/cards/${cardId}/suggestions/direct`, {
      method: 'POST', body: JSON.stringify({ ...(brief ? { brief } : {}), scope }),
    }),

  /**
   * Where this card has been.
   *
   * `rendered` says whether that configuration already has a finished video —
   * the difference between a rollback that is instant and one that costs two
   * minutes of CPU.
   */
  versions: (cardId: string) =>
    request<{ versions: CardVersion[] }>(`/cards/${cardId}/versions`).then((r) => r.versions),

  /**
   * Go back to one. Writes it forward as the current state rather than deleting
   * anything, so the rollback is itself a version and going forward again is
   * the same operation.
   */
  restoreVersion: (cardId: string, seq: number) =>
    request<{ card: Card; restoredFrom: number; version: number | null }>(
      `/cards/${cardId}/versions/${seq}/restore`, { method: 'POST', body: '{}' },
    ),

  /** Put a clip on the creator's shelf, or take it off. */
  shelveSource: (id: string, onShelf: boolean) =>
    request<{ source: Source }>(`/sources/${id}/shelf`, {
      method: 'PATCH', body: JSON.stringify({ onShelf }),
    }).then((r) => r.source),

  /** Deny everything still pending. Changes nothing about the card, records no version. */
  denyAllSuggestions: (cardId: string) =>
    request<{ denied: number }>(`/cards/${cardId}/suggestions/deny-all`, { method: 'POST', body: '{}' }),

  /** Deny this one and have the director try the same slot afresh. */
  askAgain: (cardId: string, sid: string) =>
    request<{ suggestions: Suggestion[]; detail?: string }>(`/cards/${cardId}/suggestions/${sid}/again`, {
      method: 'POST', body: '{}',
    }),

  /**
   * From a headline: the assistant writes the script and arranges the piece,
   * and the creator reviews a finished draft. The script is saved; the
   * arrangement arrives as proposals, because "arranges" still means you
   * approve.
   */
  /** 190: with approve/thumbnail/land it is the whole one shot, server-side. */
  produce: (cardId: string, opts: { speakers?: string[]; targetSeconds?: number; approve?: boolean; thumbnail?: boolean; land?: 'scripted' | 'editing'; talent?: string[] } = {}) =>
    request<{ card: Card; rundown: Turn[]; suggestions: Suggestion[]; gathered: number; detail?: string }>(
      `/cards/${cardId}/produce`, { method: 'POST', body: JSON.stringify(opts) },
    ),

  decideSuggestion: (cardId: string, sid: string, decision: 'approve' | 'deny') =>
    request<{ suggestion: Suggestion }>(`/cards/${cardId}/suggestions/${sid}/${decision}`, {
      method: 'POST', body: '{}',
    }).then((r) => r.suggestion),

  /** The voice catalogue: local, cloud (the accents), directed. */
  voices: () => request<{ voices: Voice[] }>('/voices').then((r) => r.voices),

  rooms: () => request<{ rooms: Room[]; media: boolean }>('/rooms'),
  createRoom: (input: { name: string; brandId?: string | null; channelId?: string | null }) =>
    request<{ room: Room }>('/rooms', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.room),
  updateRoom: (id: string, input: { name?: string; status?: Room['status']; channelId?: string | null; description?: string | null; rules?: string | null; pictureId?: string | null; donateUrl?: string | null; donateLabel?: string | null; superchatMin?: number; bannerPos?: BannerPos; accent?: string | null; bannerXY?: [number, number] | null; welcome?: string | null; nextAt?: string | null; theme?: 'dark' | 'light'; links?: DoorLink[] | null; plan?: PlanSegment[] | null; planStartedAt?: string | null; bannerStyle?: BannerStyle; lookId?: string | null }) =>
    request<{ room: Room }>(`/rooms/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then((r) => r.room),
  roomGuests: (id: string) => request<{ guests: RoomGuest[] }>(`/rooms/${id}/guests`).then((r) => r.guests),
  moveGuest: (roomId: string, guestId: string, move: GuestMove) =>
    request<{ guest: RoomGuest }>(`/rooms/${roomId}/guests/${guestId}/${move}`, { method: 'POST' }).then((r) => r.guest),
  roomMedia: (id: string) => request<{ media: MediaKey | null }>(`/rooms/${id}/media`).then((r) => r.media),
  /** G10: the host's comment queue, and showing or hiding one. */
  roomComments: (id: string) => request<{ comments: RoomComment[] }>(`/rooms/${id}/comments`).then((r) => r.comments),
  /** The console's pulse: guests, comments, polls and reactions in one request. */
  roomPulse: (roomId: string) => request<{ guests: RoomGuest[]; comments: RoomComment[]; polls: RoomPoll[]; reactions: Record<string, number> }>(`/rooms/${roomId}/pulse`),
  /** G17: reactions — the host reads the last few seconds' counts. */
  roomReactions: (roomId: string) => request<{ counts: Record<string, number> }>(`/rooms/${roomId}/reactions`).then((r) => r.counts),
  /** G16: polls — the host's side. */
  roomPolls: (roomId: string) => request<{ polls: RoomPoll[] }>(`/rooms/${roomId}/polls`).then((r) => r.polls),
  openPoll: (roomId: string, body: { question: string; options: string[] }) => request<{ poll: RoomPoll }>(`/rooms/${roomId}/polls`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.poll),
  closePoll: (roomId: string, pollId: string) => request<{ poll: RoomPoll }>(`/rooms/${roomId}/polls/${pollId}/close`, { method: 'POST', body: '{}' }).then((r) => r.poll),
  /** D20: the director drafts the run of show. */
  writePlan: (roomId: string, minutes: number) => request<{ plan: PlanSegment[] }>(`/rooms/${roomId}/plan/write`, { method: 'POST', body: JSON.stringify({ minutes }) }).then((r) => r.plan),
  /** D19: off the air, the director debriefs the live. */
  roomDebrief: (roomId: string) => request<{ debrief: Debrief }>(`/rooms/${roomId}/debrief`, { method: 'POST', body: '{}' }).then((r) => r.debrief),
  /** D18: the director reads the waiting comments. */
  readChat: (roomId: string) =>
    request<ChatRead>(`/rooms/${roomId}/chat/read`, { method: 'POST', body: '{}' }),
  /** G5: a line from the host onto the screen — a guest's question, a poll, a note. */
  hostLine: (roomId: string, body: { name?: string; text: string; guestId?: string | null }) =>
    request<{ comment: RoomComment }>(`/rooms/${roomId}/comments`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.comment),
  setComment: (roomId: string, commentId: string, status: 'shown' | 'hidden' | 'pending') =>
    request<{ comment: RoomComment }>(`/rooms/${roomId}/comments/${commentId}/${status}`, { method: 'POST' }).then((r) => r.comment),
  /** G2: the room goes out to its channel (and the channel's other destinations). */
  roomOnAir: (id: string, streamKey?: string) =>
    request<{ room: Room }>(`/rooms/${id}/on-air`, { method: 'POST', body: JSON.stringify(streamKey ? { streamKey } : {}) }).then((r) => r.room),
  roomOffAir: (id: string) => request<{ room: Room }>(`/rooms/${id}/off-air`, { method: 'POST', body: '{}' }).then((r) => r.room),

  /** The guest's side: no token, a secret instead. */
  greenRoom: (slug: string) => request<{ room: DoorInfo }>(`/green/${slug}`).then((r) => r.room),
  /** G10: comments from the door; a moderator's moves. */
  greenComment: (slug: string, guest: string, secret: string, text: string, amountCents?: number) =>
    request<{ id: string }>(`/green/${slug}/comment`, { method: 'POST', body: JSON.stringify({ guest, secret, text, amountCents }) }),
  greenComments: (slug: string, guest: string, secret: string) =>
    request<{ comments: RoomComment[] }>(`/green/${slug}/comments?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`).then((r) => r.comments),
  /** The door's pulse: the state and everything the page watches, in one request. */
  greenPulse: (slug: string, guest: string, secret: string) =>
    request<{ status: RoomGuest['status']; room: DoorInfo; profile?: GuestProfile; media: MediaKey | null; moderator: boolean; comments: RoomComment[]; poll: RoomPoll | null; reactions: Record<string, number>; guests: { id: string; name: string; status: RoomGuest['status']; role: string | null; question: string | null }[] }>(
      `/green/${slug}/pulse?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`),
  /** G17: a reaction from the door, and what the room felt. */
  greenReact: (slug: string, guest: string, secret: string, emoji: string) =>
    request<void>(`/green/${slug}/react`, { method: 'POST', body: JSON.stringify({ guest, secret, emoji }) }),
  greenReactions: (slug: string, guest: string, secret: string) =>
    request<{ counts: Record<string, number> }>(`/green/${slug}/reactions?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`).then((r) => r.counts),
  /** G16: the poll at the door, and a vote. */
  greenPoll: (slug: string, guest: string, secret: string) =>
    request<{ poll: RoomPoll | null }>(`/green/${slug}/poll?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`).then((r) => r.poll),
  greenVote: (slug: string, guest: string, secret: string, poll: string, choice: number) =>
    request<void>(`/green/${slug}/vote`, { method: 'POST', body: JSON.stringify({ guest, secret, poll, choice }) }),
  greenGuests: (slug: string, guest: string, secret: string) =>
    request<{ guests: { id: string; name: string; status: RoomGuest['status']; role: string | null; question: string | null }[] }>(
      `/green/${slug}/guests?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`).then((r) => r.guests),
  greenModerate: (slug: string, guest: string, secret: string, action: { target: string; move: 'admit' | 'hold' | 'remove' | 'on-air' } | { comment: string; status: 'shown' | 'hidden' }) =>
    request<void>(`/green/${slug}/moderate`, { method: 'POST', body: JSON.stringify({ guest, secret, ...action }) }),
  greenJoin: (slug: string, name: string) =>
    request<{ guestId: string; secret: string }>(`/green/${slug}/join`, { method: 'POST', body: JSON.stringify({ name }) }),
  /** G7: the guest's own profile, by their secret. */
  greenProfile: (slug: string, guest: string, secret: string, profile: { role?: string; location?: string; handle?: string; pronouns?: string; hue?: number | null; avatar?: string; question?: string; cameraOff?: boolean }) =>
    request<void>(`/green/${slug}/profile`, { method: 'POST', body: JSON.stringify({ guest, secret, ...profile }) }),
  greenState: (slug: string, guest: string, secret: string) =>
    request<{ status: RoomGuest['status']; room: DoorInfo; profile?: GuestProfile; media: MediaKey | null }>(
      `/green/${slug}/state?guest=${encodeURIComponent(guest)}&secret=${encodeURIComponent(secret)}`),
  greenLeave: (slug: string, guest: string, secret: string) =>
    request<void>(`/green/${slug}/leave`, { method: 'POST', body: JSON.stringify({ guest, secret }) }),

  /** 194: one element from one sentence, for the open look; nothing is saved until the look is. */
  labElement: (brandId: string, brief: string) =>
    request<{ element: import('./lab/recipe').Element }>('/lab/element', { method: 'POST', body: JSON.stringify({ brandId, brief }) }).then((r) => r.element),

  // 192: the template store.
  templates: (kind?: Template['kind']) =>
    request<{ templates: Template[] }>(`/templates${kind ? `?kind=${kind}` : ''}`).then((r) => r.templates),
  /** The poster through the store's door, cached like a thumb. */
  templatePoster: (id: string): Promise<string | null> => cachedPicture(`/v1/templates/${id}/poster`),
  takeTemplate: (id: string, body: { brandId?: string | null; cardId?: string | null }) =>
    request<{ asset?: { id: string }; look?: { id: string }; element?: { id: string } }>(`/templates/${id}/take`, { method: 'POST', body: JSON.stringify(body) }),

  // 191: talent.
  talent: () => request<{ talent: TalentRow[]; images: Allowance }>('/talent'),
  /** 195: back to a take they already have — no model call, no charge. */
  restorePortrait: (id: string, assetId: string) =>
    request<{ talent: TalentRow }>(`/talent/${id}/portrait/restore`, { method: 'POST', body: JSON.stringify({ assetId }) }).then((r) => r.talent),
  addTalent: (body: { name: string; sheet?: string | null; voiceId?: string | null; bio?: string | null }) =>
    request<{ talent: TalentRow }>('/talent', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.talent),
  saveTalent: (id: string, patch: { name?: string; sheet?: string | null; voiceId?: string | null; bio?: string | null; portraitAssetId?: string | null }) =>
    request<{ talent: TalentRow }>(`/talent/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).then((r) => r.talent),
  removeTalent: (id: string) => request<void>(`/talent/${id}`, { method: 'DELETE' }),
  /** The portrait from the sheet, in the show's style; a redraw keeps the face. */
  drawPortrait: (id: string, body: { prompt?: string; brandId?: string | null } = {}) =>
    request<{ asset: Asset; jobId: string | null; status: string }>(`/talent/${id}/portrait`, { method: 'POST', body: JSON.stringify(body) }),
  /** Cast on a show: voice, face and bio copied onto the show's maps. */
  castTalent: (id: string, brandId: string) =>
    request<{ brand: Brand; cast: { name: string; voice: boolean; face: boolean; bio: boolean } }>(`/talent/${id}/cast`, { method: 'POST', body: JSON.stringify({ brandId }) }),

  /** The kit bench (190): a kit proposed from a brief; the creator saves it or not. */
  designKit: (brandId: string, brief: string, logo = false) =>
    request<{ proposal: { groundHex: string; accentHex: string; wordmark: string; style: string; why: string | null }; logoAssetId: string | null; detail?: string }>(`/brands/${brandId}/design`, { method: 'POST', body: JSON.stringify({ brief, logo }) }),

  addCardPreset: (brandId: string, body: { label: string; defaults: CardDefaults; sourcing?: Sourcing; mode?: Mode }) =>
    request<{ preset: CardPreset; presets: CardPreset[] }>(`/brands/${brandId}/presets`, { method: 'POST', body: JSON.stringify(body) }),
  removeCardPreset: (brandId: string, pid: string) =>
    request<{ presets: CardPreset[] }>(`/brands/${brandId}/presets/${pid}`, { method: 'DELETE' }).then((r) => r.presets),

  addBrandElement: (brandId: string, body: { label: string; props: LayerProps; seconds?: number }) =>
    request<{ element: BrandElement; elements: BrandElement[] }>(`/brands/${brandId}/elements`, { method: 'POST', body: JSON.stringify(body) }),
  removeBrandElement: (brandId: string, eid: string) =>
    request<{ elements: BrandElement[] }>(`/brands/${brandId}/elements/${eid}`, { method: 'DELETE' }).then((r) => r.elements),

  channels: () => request<{ channels: Channel[] }>('/channels').then((r) => r.channels),
  createChannel: (input: ChannelInput) =>
    request<{ channel: Channel }>('/channels', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.channel),
  updateChannel: (id: string, input: Partial<ChannelInput>) =>
    request<{ channel: Channel }>(`/channels/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then((r) => r.channel),
  /** The key goes to the API once and is never read back. */
  startChannel: (id: string, streamKey: string) =>
    request<{ channel: Channel }>(`/channels/${id}/start`, { method: 'POST', body: JSON.stringify({ streamKey }) }).then((r) => r.channel),
  addDestination: (id: string, body: { platform: 'youtube' | 'facebook' | 'rtmp'; ingestUrl?: string; streamKey: string }) =>
    request<{ channel: Channel }>(`/channels/${id}/destinations`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.channel),
  removeDestination: (id: string, n: number) =>
    request<{ channel: Channel }>(`/channels/${id}/destinations/${n}`, { method: 'DELETE' }).then((r) => r.channel),
  scheduleChannel: (id: string, body: { startAt?: string | null; stopAt?: string | null }) =>
    request<{ channel: Channel }>(`/channels/${id}/schedule`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.channel),
  stopChannel: (id: string) =>
    request<{ channel: Channel }>(`/channels/${id}/stop`, { method: 'POST' }).then((r) => r.channel),
  /** D14: the director writes the brief; the creator edits it on the card. */
  writeBrief: (id: string) => request<{ card: Card; brief: string }>(`/cards/${id}/brief`, { method: 'POST', body: '{}' }),
  saveBrief: (id: string, brief: string | null) => request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ brief }) }).then((r) => r.card),
  /** 190: where the director may draw from, changed from the director panel. */
  setSourcing: (id: string, sourcing: Sourcing) => request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ sourcing }) }).then((r) => r.card),
  /** D9: one line rewritten to an ask; nothing saved until the creator keeps it. */
  rewriteLine: (cardId: string, index: number, ask: string) =>
    request<{ text: string }>(`/cards/${cardId}/lines/${index}/rewrite`, { method: 'POST', body: JSON.stringify({ ask }) }).then((r) => r.text),
  /** A few seconds of a cloud or directed voice, as a playable URL. Null for a local one. */
  voiceSample: async (id: string, read?: string, text?: string): Promise<string | null> => {
    const params = new URLSearchParams();
    if (read) params.set('read', read);
    if (text) params.set('text', text.slice(0, 300));
    const q = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/v1/voices/${encodeURIComponent(id)}/sample${q}`, {
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, 'could not play that voice');
    return URL.createObjectURL(await res.blob());
  },

  /** The speaker names this brand's scripts use, so a cast can be checked. */
  brandSpeakers: (id: string) =>
    request<{ speakers: string[] }>(`/brands/${id}/speakers`).then((r) => r.speakers),

  /** Publish on Rookery alone: the local copy goes to the door with no social destination. */
  publishOnRookery: (id: string) => request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ stage: 'published' }) }),
  /** On Rookery: where the episode shows at the door. */
  /** Series (142). */
  series: (brandId?: string | null) => request<{ series: Series[] }>(`/series${brandId ? `?brand=${brandId}` : ''}`).then((r) => r.series),
  planSeries: (brandId: string, subject: string) => request<{ plan: { name: string; description: string; episodes: { title: string; angle: string }[] } }>('/series/plan', { method: 'POST', body: JSON.stringify({ brandId, subject }) }).then((r) => r.plan),
  /** The Lab (153). */
  // The director's board (T229)
  directorNext: () => request<{ proposals: Proposal[]; memo: Memo | null; at: string }>('/director/next'),
  directorMemo: (force = false) => request<{ memo: Memo }>('/director/memo', { method: 'POST', body: JSON.stringify({ force }) }).then((r) => r.memo),
  // Asset groups (156)
  assetGroups: () => request<{ groups: { name: string; count: number }[]; ungrouped: number }>('/assets/groups'),
  groupAssets: (ids: string[], group: string | null) => request<{ moved: number }>('/assets/group', { method: 'POST', body: JSON.stringify({ ids, group }) }),
  // The workspace defaults (155)
  defaults: () => request<{ defaults: Defaults; at: string | null }>('/defaults'),
  saveDefaults: (d: Defaults) => request<{ defaults: Defaults; at: string | null }>('/defaults', { method: 'PUT', body: JSON.stringify(d) }),
  // Spyglass (154)
  spyglassTakes: (brandId?: string | null) => request<{ takes: ScreenTake[] }>(`/spyglass/takes${brandId ? `?brand=${brandId}` : ''}`).then((r) => r.takes),
  recordings: (brandId?: string | null) => request<{ recordings: Recording[] }>(`/spyglass/recordings${brandId ? `?brand=${brandId}` : ''}`).then((r) => r.recordings),
  recording: (id: string) => request<{ recording: Recording }>(`/spyglass/recordings/${id}`).then((r) => r.recording),
  buildRecording: (sourceId: string, name?: string) => request<{ recording: Recording }>('/spyglass/recordings', { method: 'POST', body: JSON.stringify({ sourceId, name }) }).then((r) => r.recording),
  updateRecording: (id: string, patch: { name?: string; shared?: boolean }) => request<{ recording: Recording }>(`/spyglass/recordings/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).then((r) => r.recording),
  draftTalentBio: (brandId: string, name: string) => request<{ bio: string; lines: number }>(`/brands/${brandId}/talent/${encodeURIComponent(name)}/bio`, { method: 'POST' }),
  readRecording: (id: string) => request<{ read: { title: string; summary: string; chapters: { at: number; title: string }[] } }>(`/spyglass/recordings/${id}/read`, { method: 'POST' }).then((r) => r.read),
  deleteRecording: (id: string) => request<void>(`/spyglass/recordings/${id}`, { method: 'DELETE' }),
  labPrototypes: () => request<{ prototypes: { id: string; name: string; kind: 'plate' | 'rig' | 'frame' | 'scene'; blurb: string }[] }>('/lab/prototypes').then((r) => r.prototypes),
  labLooks: (brandId?: string | null) => request<{ looks: Look[] }>(`/lab/looks${brandId ? `?brand=${brandId}` : ''}`).then((r) => r.looks),
  labLook: (id: string) => request<{ look: Look }>(`/lab/looks/${id}`).then((r) => r.look),
  createLook: (input: { brandId: string; name: string; recipe: Recipe; source?: string }) => request<{ look: Look }>('/lab/looks', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.look),
  instantiatePrototype: (id: string, brandId: string) => request<{ look: Look }>(`/lab/prototypes/${id}/instantiate`, { method: 'POST', body: JSON.stringify({ brandId }) }).then((r) => r.look),
  updateLook: (id: string, input: { name?: string; recipe?: Recipe; shared?: boolean }) => request<{ look: Look }>(`/lab/looks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then((r) => r.look),
  deleteLook: (id: string) => request<void>(`/lab/looks/${id}`, { method: 'DELETE' }),
  renderLook: (id: string) => request<{ status: string; jobId: string; assets: Record<string, string> }>(`/lab/looks/${id}/render`, { method: 'POST', body: '{}' }),
  labFrame: async (id: string, t: number, recipe?: Recipe): Promise<string> => {
    const res = await fetch(`/v1/lab/looks/${id}/frame`, { method: 'POST', headers: { Authorization: `Bearer ${getToken() ?? ''}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ t, scale: 0.5, ...(recipe ? { recipe } : {}) }) });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({ error: 'the frame could not be drawn' }))).error ?? 'the frame could not be drawn');
    return URL.createObjectURL(await res.blob());
  },
  directLook: (input: { brandId: string; skeleton: string; brief: string; frames: { mimeType: string; data: string }[] }) => request<{ look: Look; fromModel: boolean }>('/lab/direct', { method: 'POST', body: JSON.stringify(input) }),
  labGallery: () => request<{ looks: { id: string; kind: string; name: string; creator: string; updatedAt: string; poster: string | null; elements: number; plate: string | null }[] }>('/lab/gallery').then((r) => r.looks),
  useGalleryLook: (id: string, brandId: string) => request<{ look: Look }>(`/lab/gallery/${id}/use`, { method: 'POST', body: JSON.stringify({ brandId }) }).then((r) => r.look),
  createSeries: (input: { brandId: string; name: string; description?: string | null }) => request<{ series: Series }>('/series', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.series),
  setCardSeries: (id: string, seriesId: string | null, seriesOrder: number | null) => request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ seriesId, seriesOrder }) }).then((r) => r.card),
  setCardVisibility: (id: string, visibility: 'public' | 'members' | 'unlisted') =>
    request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ visibility }) }),
  saveBrandKit: (id: string, kit: { bio?: string | null; avatarAssetId?: string | null; accentHex?: string | null; groundHex?: string | null; wordmark?: string | null; style?: string | null;
                                    voices?: Record<string, VoiceId> | null;
                                    /** The cast's faces: name to asset id. */
                                    portraits?: Record<string, string> | null;
                                    /** Who each of them is. */
                                    bios?: Record<string, string> | null;
                                    /** Where to follow the show (086). */
                                    handles?: Partial<Record<SocialPlatform, string>> | null;
                                    /** The show's logos (097): picture asset ids. */
                                    logos?: string[] | null;
                                    /** The transitions this brand keeps to hand. */
                                    shelfTransitions?: string[];
                                    /** How a new card starts. `{}` clears it. */
                                    cardDefaults?: CardDefaults;
                                    /** D17: the show's language, BCP-47; null is English. */
                                    language?: string | null;
  }) =>
    request<{ brand: Brand }>(`/brands/${id}`, { method: 'PATCH', body: JSON.stringify(kit) })
      .then((r) => r.brand),

  /**
   * Set or clear the card's music bed.
   *
   * Sends an assetId, never a key: the API resolves the key from a row RLS has
   * already scoped, so a mix cannot name audio from another workspace even if
   * this code asked it to. `null` removes the bed — deliberately distinct from
   * sending nothing, which would mean "leave it alone".
   */
  setMix: (cardId: string, music: MixInput | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ music }),
    }).then((r) => r.card),

  /**
   * The backdrop, set or cleared, WITHOUT touching the music.
   *
   * Sent as its own field for that reason: the route merges rather than
   * replaces, and omitting a field means "leave it alone" while sending null
   * means "take it off". One call that sent both would clear the bed every time
   * somebody changed the picture.
   */
  /** Whether each spoken line is shown on screen. */
  setPanels: (cardId: string, panels: 'card' | 'box') =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, { method: 'PATCH', body: JSON.stringify({ panels }) }).then((r) => r.card),

  /** F2: the boxes' border line and width; null goes back to the light default. */
  setBox: (cardId: string, box: { line?: string; width?: number } | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, { method: 'PATCH', body: JSON.stringify({ box }) }).then((r) => r.card),
  setCaptions: (cardId: string, captions: boolean) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ captions }),
    }).then((r) => r.card),

  /** The Lab (153): a made look's overlay over the finished cut; null takes it off. */
  setLook: (cardId: string, lookId: string | null) => request<{ card: Card }>(`/cards/${cardId}/mix`, { method: 'PATCH', body: JSON.stringify({ look: lookId ? { id: lookId } : null }) }).then((r) => r.card),
  /** The finished cut's loudness, in LUFS. Null returns to the default. */
  setLoudness: (cardId: string, loudness: number | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ loudness }),
    }).then((r) => r.card),

  /** How hard the encoder works. Null returns to standard. */
  setQuality: (cardId: string, quality: QualityName | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ quality }),
    }).then((r) => r.card),

  /** How the burned-in line looks. Null returns to the house caption. */
  setCaptionStyle: (cardId: string, captionStyle: CaptionStyle | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ captionStyle }),
    }).then((r) => r.card),

  setBackdrop: (cardId: string, background: BackdropInput | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ background }),
    }).then((r) => r.card),

  /** Rename, or write a note. Sends only what changed. */
  updateAsset: (id: string, patch: { name?: string | null; notes?: string | null; group?: string | null }) =>
    request<{ asset: Asset }>(`/assets/${id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }).then((r) => r.asset),

  /** Remove the row and its bytes. Refused while a card still uses it. */
  deleteAsset: (id: string) =>
    request<void>(`/assets/${id}`, { method: 'DELETE' }),

  /**
   * A short-lived thumbnail URL, or null when there is nothing to show.
   *
   * 404 is the ordinary answer for a document, or a video still being
   * promoted, so it resolves to null rather than throwing — a listing should
   * draw a placeholder, not an error.
   */
  /**
   * The thumb (092): a few kilobytes from the API, kept in the browser's
   * Cache Storage and in memory, so a visit to Assets costs nothing the
   * second time. Resolves to an object URL, or null when there is none.
   */
  assetThumbnail: (id: string): Promise<string | null> => cachedPicture(`/v1/assets/${id}/thumb`),
  /** A card's picture for the board — its chosen thumbnail or the render's — cached the same way. */
  cardThumbnail: (id: string): Promise<string | null> => cachedPicture(`/v1/cards/${id}/thumb`),
  /** A source's thumb (a frame from one second in), cached the same way. */
  sourceThumbnail: (id: string): Promise<string | null> => cachedPicture(`/v1/sources/${id}/thumb`),


  /**
   * How one turn joins the next. Sent on its own so it cannot disturb the
   * music or the backdrop — the route merges, and absent means "leave it".
   */
  setTransition: (cardId: string, transition: { kind: TransitionKind; seconds?: number } | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ transition }),
    }).then((r) => r.card),

  /** How the programme ends: a fade to black over its last seconds, or null for the hard stop. */
  setFadeOut: (cardId: string, fadeOut: number | null) =>
    request<{ card: Card }>(`/cards/${cardId}/mix`, {
      method: 'PATCH', body: JSON.stringify({ fadeOut }),
    }).then((r) => r.card),

  /**
   * Make a library asset usable as footage a turn can cut to.
   *
   * No upload and no second copy: the bytes already crossed the uploads/media
   * boundary when the asset was promoted, and the API takes the key from the
   * asset row rather than from us — see 059, which exists so the app role never
   * names a storage key.
   */
  sourceFromAsset: (assetId: string, name: string, kind: 'tape' | 'file' = 'tape') =>
    request<{ source: Source }>('/sources', {
      method: 'POST', body: JSON.stringify({ assetId, name, kind }),
    }).then((r) => r.source),

  assets: (brandId?: string | null) =>
    request<{ assets: Asset[] }>(`/assets${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`)
      .then((r) => r.assets),

  /**
   * A picture from a description, into the library. It arrives promoting, like
   * an upload, and is usable once the render plane has looked at it.
   */
  generateAsset: (prompt: string, brandId: string | null, opts: {
    /** 190: ready pictures to keep to — a character's portrait keeps its face and clothes. Three at most. */
    references?: string[]; size?: '1K' | '2K' | '4K';
    /** 204: the catalogue row to make it on, and the priced variant. Absent is the workspace's model at its default. */
    model?: string; resolution?: string; quality?: string;
    /** The picture's shape, from the model's list (16:9 for a still in a programme); absent is the model's default, usually square. */
    aspectRatio?: string;
    /** 191: who is in the picture — their sheets in the prompt, their portraits as references. Three at most. */
    talent?: string[];
    /** File it into this episode's collection, not only the library. */
    cardId?: string; group?: string;
  } = {}) =>
    request<{ asset: Asset; jobId: string | null; status: string }>('/assets/generate', {
      method: 'POST', body: JSON.stringify({ prompt, brandId, ...opts }),
    }).then((r) => r.asset),

  /** 196: lines in one voice, filed as one audio asset. Promoting, like an upload. */
  // --- the Video Lab (197) ---------------------------------------------
  // --- the Scene Builder (199) ------------------------------------------
  /** The director breaks a premise into shots. A proposal: nothing is written or bought. */
  planScenes: (body: { premise: string; cast: string[]; scenes?: number }) =>
    request<{ scenes: PlannedScene[]; warnings: string[]; voiceless: string[]; seconds: number }>(
      '/video/plan', { method: 'POST', body: JSON.stringify(body) }),
  /** Commission the shots. One job each, so a shot that fails costs one shot. */
  buildPlan: (body: {
    name: string; premise: string; brandId: string | null;
    /** 204: a catalogue id from the picker, never a vendor string; the finish on it. Absent is the workspace's model at its default. */
    model?: string; resolution?: string; audio?: boolean;
    scenes: Array<{ beat: string; action: string; cast: string[]; speaker?: string | null; line?: string | null }>;
  }) => request<{ planId: string; scenes: SceneRow[]; queued: number; seconds: number; model?: { id: string; name: string }; credits?: number }>(
      '/video/plans', { method: 'POST', body: JSON.stringify(body) }),
  scenePlans: (brandId: string | null) =>
    request<{ plans: ScenePlan[] }>(`/video/plans${brandId ? `?brandId=${brandId}` : ''}`).then((r) => r.plans),
  scenePlan: (id: string) =>
    request<{ plan: ScenePlan; scenes: Array<SceneRow & { video: string | null; poster: string | null }> }>(`/video/plans/${id}`),
  deleteScenePlan: (id: string) => request<void>(`/video/plans/${id}`, { method: 'DELETE' }),

  /** The clips this library has built, newest first. */
  clips: (brandId: string | null) => request<{ clips: Clip[] }>(`/video/clips${brandId ? `?brandId=${brandId}` : ''}`).then((r) => r.clips),
  /** One clip, with signed URLs for the file and the poster once they are ready. */
  clip: (id: string) => request<{ clip: Clip & { video: string | null; poster: string | null } }>(`/video/clips/${id}`).then((r) => r.clip),
  /** An illustrated clip: beats with the cast as references, spoken in their voices, a bed under, joins between. */
  makeClip: (body: ClipRequest) =>
    request<{ clip: Clip; jobId: string; pictures: number; chars: number }>('/video/clips', { method: 'POST', body: JSON.stringify(body) }),
  /** The row goes; the clip, poster and pictures stay in the library. */
  deleteClip: (id: string) => request<void>(`/video/clips/${id}`, { method: 'DELETE' }),
  speak: (body: { lines: Array<{ text: string; read?: string }>; talentId?: string | null; voiceId?: string | null; brandId?: string | null; cardId?: string; group?: string; name?: string }) =>
    request<{ asset: Asset; jobId: string | null; status: string; seconds: number; chars: number }>('/audio/speak', { method: 'POST', body: JSON.stringify(body) }),

  // --- the catalogue and the credits (204) ---------------------------------
  /** What a bench offers this workspace, with the workspace's own default marked. */
  models: (bench: Bench) => request<{ bench: Bench; models: ModelInfo[] }>(`/models?bench=${bench}`).then((r) => r.models),
  /** The price of one press, before it, against the balance. Never writes, never refuses. */
  quote: (body: { bench: Bench | 'render'; model?: string; inputs?: { resolution?: string; quality?: string; op?: EditOp; scale?: 2 | 4; seconds?: number; audio?: boolean; shots?: number; minutes?: number; inputTokens?: number; outputTokens?: number; cachedTokens?: number; chars?: number } }) =>
    request<Quote>('/quote', { method: 'POST', body: JSON.stringify(body) }),
  /** What the workspace holds, its lots, its plan and the packs it may buy. */
  credits: () => request<CreditBalance>('/credits'),
  creditLedger: (q: { from?: string; to?: string; cursor?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.from) p.set('from', q.from); if (q.to) p.set('to', q.to); if (q.cursor) p.set('cursor', q.cursor); if (q.limit) p.set('limit', String(q.limit));
    return request<{ rows: LedgerRow[]; next: string | null }>(`/credits/ledger${p.toString() ? `?${p}` : ''}`);
  },
  creditSummary: (days = 30) => request<CreditSummary>(`/credits/summary?days=${days}`),
  /** The ledger as a file: the same route, asked for CSV. */
  creditsCsv: async (from?: string, to?: string) => {
    const token = getToken();
    const p = new URLSearchParams(); if (from) p.set('from', from); if (to) p.set('to', to);
    const res = await fetch(`/v1/credits/ledger${p.toString() ? `?${p}` : ''}`, { headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    if (!res.ok) throw new ApiError(res.status, 'the ledger could not be downloaded');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `credits-${(from ?? 'all').slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  },
  /** A pack at the plan's own rate, paid at Stripe. */
  buyPack: (packId: string) => request<{ url: string }>('/credits/checkout', { method: 'POST', body: JSON.stringify({ packId }) }),
  /**
   * A NEW picture derived from this one: changed by a prompt, upscaled,
   * with its background removed, or combined with others. The source is never
   * touched; the result names it. 409 `no-provider` when an op is off here.
   */
  editAsset: (id: string, body: { op: EditOp; model?: string; resolution?: string; prompt?: string; scale?: 2 | 4; references?: string[]; brandId?: string | null; cardId?: string; group?: string }) =>
    request<{ asset: Asset; jobId: string | null; status: 'promoting'; derivedFrom: string }>(`/assets/${id}/edit`, { method: 'POST', body: JSON.stringify(body) }),
  /** A card from ordered library assets and an optional bed, in one transaction. */
  assembleCard: (body: { title: string; brandId?: string | null; items: Array<{ assetId: string; holdSeconds?: number }>; bedAssetId?: string }) =>
    request<{ card: Card }>('/cards/assemble', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.card),

  /** 193: a bed from a description, into the library (paid plans). Promoting, like an upload. */
  generateBed: (prompt: string, brandId: string | null, opts: { avoid?: string; cardId?: string; group?: string; model?: string } = {}) =>
    request<{ asset: Asset; jobId: string | null; status: string; seconds: number; model?: { id: string; name: string } }>('/assets/generate-bed', {
      method: 'POST', body: JSON.stringify({ prompt, brandId, ...opts }),
    }),

  /** Look at the picture and label what it shows, so the director can match narration to it. */
  describeAsset: (id: string) =>
    request<{ asset: Asset; described: { labels: string[]; description: string } }>(`/assets/${id}/describe`, {
      method: 'POST', body: '{}',
    }).then((r) => r.asset),

  createAsset: (input: { brandId: string | null; filename: string; kind: Asset['kind']; contentType: string }) =>
    request<{ asset: Asset; upload: SignedUrl }>('/assets', { method: 'POST', body: JSON.stringify(input) }),

  assetUrl: (id: string) => request<{ download: SignedUrl }>(`/assets/${id}/url`).then((r) => r.download),
  /** A document has nothing to render it with, so it is handed over as a file. */
  downloadAsset: (id: string, filename: string) => download(`/assets/${id}/file`, filename),

  /** What this deployment can do. Readable signed out; see the API comment. */
  capabilities: () => request<{
    passwordReset: boolean;
    youtubePublishing: boolean;
    billing: boolean;
    /** Whether a recording can be transcribed into scenes on this deployment. */
    transcription?: boolean;
    platforms: PlatformReport[];
  }>('/capabilities'),

  requestReset: (email: string) =>
    request<{ ok: boolean; message: string }>('/auth/reset', {
      method: 'POST', body: JSON.stringify({ email }),
    }),
  confirmReset: (token: string, password: string) =>
    request<{ ok: boolean; message: string }>('/auth/reset/confirm', {
      method: 'POST', body: JSON.stringify({ token, password }),
    }),
  closeWorkspace: (password: string, confirmSlug: string) =>
    request<{ closed: boolean; objectsRemoved: number; accountDeleted: boolean; note: string }>(
      '/auth/close', { method: 'POST', body: JSON.stringify({ password, confirmSlug }) },
    ),
  signup: (body: { email: string; password: string; name?: string; workspace: string }) =>
    request<LoginResult>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
  login: (email: string, password: string, tenantId?: string) =>
    request<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(tenantId ? { tenantId } : {}) }),
    }),

  /** Billing (123): the workspace's own, the public plans, and the three pages Stripe hosts. */
  billing: () => request<BillingSummary>('/billing'),
  plans: () => request<{ plans: PlanOut[]; billing: boolean; credit: { vendorUsdPerCredit: number }; examples: Array<{ bench: string; modelName: string; variant: string; credits: number; unit: string }> }>('/plans'),
  checkout: (priceId: string) => request<{ url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ priceId }) }),
  portal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  connect: () => request<{ url: string }>('/billing/connect', { method: 'POST' }),
  driveAuthorize: () => request<{ url: string }>('/oauth/drive/authorize'),
  archiveKeep: (keepOurCopy: boolean) =>
    request<{ keepOurCopy: boolean }>('/billing/archive', { method: 'POST', body: JSON.stringify({ keepOurCopy }) }),
  archiveDisconnect: () => request<{ disconnected: boolean }>('/billing/archive', { method: 'DELETE' }),
  /** Your show this week (134): the weekly email's switch. */
  creatorDigest: () => request<{ creatorDigest: boolean }>('/auth/me/notify').then((r) => r.creatorDigest),
  setCreatorDigest: (on: boolean) => request<{ creatorDigest: boolean }>('/auth/me/notify', { method: 'PATCH', body: JSON.stringify({ creatorDigest: on }) }).then((r) => r.creatorDigest),
  /** What the door answers now: the public research door, scoped to one of this workspace's shows. */
  doorAnswer: (question: string, creator: string) => request<{ answerable: boolean; answer: string; citations: { n: number; quote: string; slug: string; title: string; at: number }[] }>('/catalogue/research', { method: 'POST', body: JSON.stringify({ question, creator }) }),
  /** What viewers ask (136): research questions about this workspace's shows and episodes. */
  planWeek: () => request<WeekPlan>('/door/plan', { method: 'POST', body: '{}' }),
  weekPlan: () => request<WeekPlan>('/door/plan'),
  doorQuestions: () => request<{ questions: { question: string; scope: string; slug: string | null; hits: number; answerable: boolean; askedAt: string }[] }>('/door/questions').then((r) => r.questions),
  /** Your show on Rookery: what is set, what is missing, what the door reports. */
  doorStatus: () => request<DoorStatus>('/door/status'),
  doorEpisodes: () => request<{ episodes: DoorEpisode[] }>('/door/episodes').then((r) => r.episodes),
  /** Inside the episode (126): the index panel. */
  cardIndex: (cardId: string) => request<CardIndex>(`/cards/${cardId}/index`),
  patchCardIndex: (cardId: string, body: { remove?: string[]; restore?: string[]; add?: { kind: ConceptKind; term: string; at?: number }[] }) =>
    request<{ ok: true }>(`/cards/${cardId}/index`, { method: 'PATCH', body: JSON.stringify(body) }),
  rebuildCardIndex: (cardId: string) => request<{ status: string }>(`/cards/${cardId}/index/rebuild`, { method: 'POST' }),
  /** Paid memberships (125): the creator's tiers and members. */
  members: () => request<MembersSummary>('/billing/members'),
  createTier: (t: { brandId: string; name: string; description?: string; amount: number; interval: 'month' | 'year' }) =>
    request<{ id: string; ready: boolean }>('/billing/tiers', { method: 'POST', body: JSON.stringify(t) }),
  patchTier: (id: string, patch: { name?: string; description?: string | null; active?: boolean }) =>
    request<{ id: string }>(`/billing/tiers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** The door's handoff code, exchanged for a session (122). */
  handoff: (code: string) => request<LoginResult>('/auth/handoff', { method: 'POST', body: JSON.stringify({ code }) }),
  /** Viewer comments on a published card, every status; the creator shows or hides. */
  cardComments: (cardId: string) => request<{ comments: CardComment[] }>(`/cards/${cardId}/comments`),
  readComments: (cardId: string) => request<{ read: { summary: string; mood: 'warm' | 'mixed' | 'critical'; questions: string[]; requests: string[]; praise: string[]; comments: number } }>(`/cards/${cardId}/comments/read`, { method: 'POST' }).then((r) => r.read),
  draftAnswer: (cardId: string, commentId: string) => request<{ draft: { answer: string; grounded: boolean } }>(`/cards/${cardId}/comments/${commentId}/draft`, { method: 'POST', body: '{}' }).then((r) => r.draft),
  answerComment: (cardId: string, body: string, parentId: string | null) =>
    request<{ comment: CardComment }>(`/cards/${cardId}/comments`, { method: 'POST', body: JSON.stringify({ body, parentId }) }),
  moderateComment: (cardId: string, id: string, status: 'shown' | 'hidden') =>
    request<{ id: string; status: string }>(`/cards/${cardId}/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  me: () => request<{ user: Account; tenant: Workspace; workspaces: Workspace[] }>('/auth/me'),

  /** The queue, the tier caps, and how much of the month is spent. */
  renders: () => request<{
    jobs: RenderJob[];
    limits: { max_concurrent_renders: number; running_now: number } | null;
    budget: { usedMinutes: number; limitMinutes: number | null };
  }>('/renders'),

  changePassword: (current: string, next: string) =>
    request<{ ok: boolean; token: string }>('/auth/password', {
      method: 'POST', body: JSON.stringify({ current, next }),
    }),

  invite: (body: { email: string; password: string; role: string; name?: string }) =>
    request<{ userId: string; email: string; role: string }>('/auth/invite', {
      method: 'POST', body: JSON.stringify(body),
    }),

  switchWorkspace: (tenantId: string) =>
    request<{ token: string; tenant: Workspace }>('/auth/switch', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    }),

  /** D12: the published hooks that earned the most views, for the next brief. */
  hooksWorked: (brandId?: string | null) => request<{ worked: Array<{ card_id: string; title: string; topic: string | null; pain_point: string | null; views: number; platform: string }> }>(`/hooks/worked${brandId ? `?brandId=${brandId}` : ''}`).then((r) => r.worked),
  hooks: (input: { painPoint: string; topic?: string; platform?: string; brandId?: string | null; targetSeconds?: number }) =>
    request<{ variants: HookVariant[] }>('/hooks', { method: 'POST', body: JSON.stringify(input) })
      .then((r) => r.variants),

  card: (id: string) => request<CardDetail>(`/cards/${id}`),

  authorizeYouTube: (connectorId: string) =>
    request<{ url: string; expiresInSeconds: number }>(
      `/oauth/youtube/authorize?connectorId=${encodeURIComponent(connectorId)}`),

  promoteAsset: (id: string) =>
    request<{ status: string; jobId: string | null }>(`/assets/${id}/promote`, {
      method: 'POST', body: '{}',
    }),

  publications: () =>
    request<{ publications: ScheduledPublication[]; blocked: number }>('/publications'),

  sources: (brandId?: string | null) =>
    request<{ sources: Source[]; kinds: SourceKind[] }>(
      `/sources${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),

  createSource: (body: {
    name: string; kind: SourceKind; brandId?: string | null;
    mode?: 'stored'|'live'; filename?: string; contentType?: string;
  }) => request<{ source: Source; upload?: SignedUrl; streamKey?: string; note?: string }>(
    '/sources', { method: 'POST', body: JSON.stringify(body) }),

  /** The source to watch: a signed URL of the normalised copy, once ingested. */
  sourceMedia: (id: string) => request<{ media: SignedUrl; name: string }>(`/sources/${id}/media`),

  /** The source's own sound as a wav, when ingest wrote one beside it. */
  sourceAudio: (id: string) => request<{ audio: SignedUrl }>(`/sources/${id}/audio`).then((r) => r.audio),

  /** What was read on a camera take (090); the director matches scenes to it. */
  /** SN3: chapters on a take from its words; the words of each chapter for an episode's scenes. */
  sourceChapters: (id: string) => request<{ chapters: DemoChapter[] }>(`/sources/${id}/chapters`, { method: 'POST', body: '{}' }).then((r) => r.chapters),
  chapterLines: (id: string) => request<{ lines: { at: number; to: number; title: string; text: string }[] }>(`/sources/${id}/chapters/lines`).then((r) => r.lines),
  readSource: (id: string) => request<{ read: { title: string; summary: string; chapters: { at: number; title: string }[] } }>(`/sources/${id}/read`, { method: 'POST' }).then((r) => r.read),
  updateSource: (id: string, body: { name?: string; said?: string | null }) =>
    request<{ source: Source }>(`/sources/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.source),

  ingestSource: (id: string) =>
    request<{ jobId: string | null; status: string }>(`/sources/${id}/ingest`, { method: 'POST', body: '{}' }),

  /** Listen to a source and write down what is said, with times. */
  transcribeSource: (id: string) =>
    request<{ jobId: string | null; status: string }>(`/sources/${id}/transcribe`, { method: 'POST', body: '{}' }),

  sourceTranscript: (id: string) =>
    request<{ transcript: Transcript }>(`/sources/${id}/transcript`).then((r) => r.transcript),

  /**
   * Scenes from a transcribed recording: each one a stretch of the source,
   * played with its own sound, its words as its captions.
   */
  scenesFromSource: (cardId: string, body: {
    sourceId: string; speaker?: string; mode?: 'replace' | 'append'; maxSceneSeconds?: number;
  }) => request<{ card: Card; rundown: Turn[]; scenes: number; estimateSeconds: number }>(
    `/cards/${cardId}/rundown/from-source`, { method: 'POST', body: JSON.stringify(body) }),

  deleteSource: (id: string) =>
    fetch(`/v1/sources/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    }).then((r) => { if (!r.ok && r.status !== 204) throw new ApiError(r.status, 'could not delete'); }),

  connectors: () =>
    request<{ connectors: Connector[]; platforms: PlatformReport[] }>('/connectors'),

  /**
   * Remove a destination. Destroys the encrypted refresh token with it, so
   * reconnecting means a fresh consent — and Google often withholds a refresh
   * token on a repeat grant, which is why the UI says so before doing it.
   */
  removeConnector: (id: string) =>
    request<void>(`/connectors/${id}`, { method: 'DELETE' }),

  addConnector: (brandId: string, platform: string, accountHandle?: string) =>
    request<{ connector: Connector }>('/connectors', {
      method: 'POST', body: JSON.stringify({ brandId, platform, accountHandle }),
    }),

  schedule: (id: string, connectorIds: string[], scheduledFor: string | null, what?: { kind: 'teaser' | 'short'; jobId: string }) =>
    request<{ card: Card; publications: Publication[] }>(`/cards/${id}/schedule`, {
      method: 'POST', body: JSON.stringify({ connectorIds, scheduledFor, ...(what ?? {}) }),
    }),

  unschedule: (id: string, publicationId: string) =>
    fetch(`/v1/cards/${id}/schedule/${publicationId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    }).then((r) => { if (!r.ok && r.status !== 204) throw new ApiError(r.status, 'could not unschedule'); }),

  writeScript: (id: string, speakers: string[] | undefined, targetSeconds: number) =>
    request<{ rundown: Turn[]; estimateSeconds: number; card: Card }>(`/cards/${id}/script`, {
      // No cast sent means the card's, then the show's; an empty list is refused by the API, so it is never sent.
      method: 'POST', body: JSON.stringify({ ...(speakers?.length ? { speakers } : {}), targetSeconds }),
    }),

  saveRundown: (id: string, rundown: Turn[]) =>
    request<{ rundown: Turn[]; estimateSeconds: number; card: Card }>(`/cards/${id}/rundown`, {
      method: 'PUT', body: JSON.stringify({ rundown }),
    }),

  /** `again` renders an unchanged card afresh instead of handing back its finished cut. */
  render: (id: string, opts: { again?: boolean } = {}) =>
    request<{ jobId: string; reused: boolean; card: Card }>(`/cards/${id}/render`, {
      method: 'POST', body: JSON.stringify(opts.again ? { again: true } : {}),
    }),

  cancelRender: (id: string) =>
    request<{ status: string | null }>(`/cards/${id}/render/cancel`, { method: 'POST', body: '{}' }),

  preview: (id: string) => request<{ download: SignedUrl }>(`/cards/${id}/preview`),

  thumbnail: (id: string) => request<{ download: SignedUrl; chosen?: boolean }>(`/cards/${id}/thumbnail`),
  /**
   * Choose the card's thumbnail: a frame of a scene under a headline, a
   * picture made from the theme (or edited from a frame), or a picture from
   * the library. Clearing goes back to the render's own.
   */
  setThumbnail: (id: string, body:
    | { kind: 'frame'; line: number; headline?: string; kicker?: string; tag?: string; stamp?: boolean; template?: 'frame' | 'faces' | 'set' | 'split' | 'cards'; pictures?: string[]; rundown?: Turn[] }
    | { kind: 'made'; prompt?: string; fromLine?: number; cutout?: boolean; headline?: string; tag?: string; stamp?: boolean; rundown?: Turn[] }
    | { kind: 'asset'; assetId: string }) =>
    request<{ card: Card; download: SignedUrl }>(`/cards/${id}/thumbnail`, { method: 'POST', body: JSON.stringify(body) }),
  clearThumbnail: (id: string) =>
    request<{ card: Card }>(`/cards/${id}/thumbnail`, { method: 'DELETE' }).then((r) => r.card),
  /**
   * Returns the caption track as a file the browser has already received,
   * rather than a URL for it to go and fetch. The difference matters: a signed
   * URL is a bearer credential, and navigating to one leaves it in the address
   * bar and in history long after the tab is closed.
   */
  captions: (id: string) => download(`/cards/${id}/captions`, `captions-${id}.vtt`),
  /** The finished cut's sound alone, as the MP3 the render wrote beside it. */
  audio: (id: string) => download(`/cards/${id}/audio`, `audio-${id}.mp3`),
  preflight: (id: string) =>
    request<{ findings: Finding[] }>(`/cards/${id}/preflight`).then((r) => r.findings),
  chapters: (id: string) =>
    request<{ chapters: Chapter[] }>(`/cards/${id}/chapters`).then((r) => r.chapters),
  writeSeo: (id: string) =>
    request<{ seo: SeoPack; chapters: Chapter[] }>(`/cards/${id}/seo`, { method: 'POST' }),
  saveSeo: (id: string, pack: { title?: string | null; description?: string | null; tags?: string[] | null }) =>
    request<{ seo: SeoPack }>(`/cards/${id}/seo`, { method: 'PATCH', body: JSON.stringify(pack) })
      .then((r) => r.seo),
  shortCandidates: (id: string) =>
    request<{ moments: Moment[]; pick: ShortPick | null; picks: ShortPick[] }>(`/cards/${id}/short-candidates`),
  /** S8: the teaser — three moments the director picked, with an end card, cut from the finished video. */
  cutTeaser: (id: string) =>
    request<{ job: { id: string; reused: boolean }; moments: { start: number; duration: number; why: string }[] }>(`/cards/${id}/teaser`, { method: 'POST', body: '{}' }),
  cutShort: (id: string, start: number, duration: number) =>
    request<{ job: { id: string; reused: boolean } }>(`/cards/${id}/short`, {
      method: 'POST',
      body: JSON.stringify({ start, duration }),
    }),
  shortUrl: (id: string, jobId: string) =>
    request<{ download: SignedUrl }>(`/cards/${id}/shorts/${jobId}/url`).then((r) => r.download),

  collection: (id: string) => request<{ items: CollectionItem[] }>(`/cards/${id}/collection`).then((r) => r.items),
  collect: (id: string, what: { assetId?: string; sourceId?: string; note?: string }) =>
    request<{ items: CollectionItem[] }>(`/cards/${id}/collection`, { method: 'POST', body: JSON.stringify(what) }).then((r) => r.items),
  uncollect: (id: string, itemId: string) => request<void>(`/cards/${id}/collection/${itemId}`, { method: 'DELETE' }),
  gatherCollection: (id: string) =>
    request<{ items: CollectionItem[]; gathered: number }>(`/cards/${id}/collection/gather`, { method: 'POST', body: '{}' }),

  cards: () => request<{ cards: Card[]; stages: Stage[] }>('/cards'),

  createCard: (input: { title: string; brandId: string | null; topic?: string; painPoint?: string; presetId?: string; sourcing?: Sourcing }) =>
    request<{ card: Card }>('/cards', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.card),

  deleteCard: (id: string) =>
    request<void>(`/cards/${id}`, { method: 'DELETE' }),

  renameSource: (id: string, name: string) =>
    request<{ source: Source }>(`/sources/${id}`, {
      method: 'PATCH', body: JSON.stringify({ name }),
    }).then((r) => r.source),

  moveCard: (id: string, stage: Stage) =>
    request<{ card: Card }>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify({ stage }) })
      .then((r) => r.card),
};

/**
 * Upload straight to Cloud Storage with the signed URL. The bytes never pass
 * through the API, which is what keeps a 2 GB master from occupying a request
 * handler for ten minutes.
 */
export async function uploadToSignedUrl(url: string, file: File, onProgress?: (pct: number) => void): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.send(file);
  });
}

/**
 * What kind of asset a file is, from its MIME type — and from its extension
 * when the browser offers none. That is not a corner case: an .m4a dragged
 * in, or handed over by a tool, routinely arrives as an empty type or
 * application/octet-stream, and it used to become a DOCUMENT — something no
 * track can take, for the most common phone audio format there is. The
 * render plane decides what the bytes really are at promotion; this only
 * decides which shelf the row starts on.
 */
export function kindFor(file: Pick<File, 'type' | 'name'>): Asset['kind'] {
  const t = file.type.toLowerCase();
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t.startsWith('image/')) return 'image';
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  if (['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', 'mpg', 'mpeg', 'mts', 'ts', 'wmv'].includes(ext)) return 'video';
  if (['m4a', 'mp3', 'wav', 'aac', 'flac', 'ogg', 'oga', 'opus', 'aif', 'aiff', 'wma', 'caf'].includes(ext)) return 'audio';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif', 'tif', 'tiff', 'bmp', 'avif'].includes(ext)) return 'image';
  return 'document';
}

/** D17: the languages a show can speak — the locales the voices offer. */
export const SHOW_LANGUAGES = [
  { tag: 'en', label: 'English' }, { tag: 'en-GB', label: 'English (UK)' }, { tag: 'en-IN', label: 'English (India)' }, { tag: 'en-AU', label: 'English (Australia)' },
  { tag: 'es-ES', label: 'Spanish (Spain)' }, { tag: 'es-US', label: 'Spanish (Americas)' }, { tag: 'pt-BR', label: 'Portuguese (Brazil)' },
  { tag: 'fr-FR', label: 'French' }, { tag: 'fr-CA', label: 'French (Canada)' }, { tag: 'de-DE', label: 'German' }, { tag: 'it-IT', label: 'Italian' }, { tag: 'hi-IN', label: 'Hindi' },
] as const;
